/**
 * WhiteLabelEditor — per-tenant brand overrides editor with always-visible
 * preview (sticky rail by default, optional floating PIP).
 *
 * Layout:
 *   ┌────────────────────────────────┬────────────────────────────────────┐
 *   │ FORM RAIL (scrolls)            │ PREVIEW RAIL (sticky)              │
 *   │ • Top explainer card           │ • Surface tabs (Login / Home /     │
 *   │ • "How this page works" panel  │   Chat / Sidebar / Modal)          │
 *   │ • Section: Starter presets     │ • Surface tab strip + pop-out      │
 *   │ • Section: Identity            │ • "Pop out" → mounts floating PIP  │
 *   │ • Section: Login surface       │   that stays visible during scroll │
 *   │ • Section: Colors              │   and across whole-page navigation │
 *   │ • Section: Typography          │                                    │
 *   │ • Section: Logo                │                                    │
 *   │ • Section: Icon                │                                    │
 *   │ • Section: Agent colors        │                                    │
 *   │ • Sticky save bar (diff hover) │                                    │
 *   └────────────────────────────────┴────────────────────────────────────┘
 *
 * Each Field declares which preview surfaces it affects (`affects` prop).
 * The form uses this for three things:
 *   1. Render a "Shows on:" badge under each field — clickable, jumps the
 *      preview pane to the surface.
 *   2. When a field is focused, switch the preview to the surface that
 *      visualizes the change.
 *   3. Optionally an explanatory paragraph ("Why change this?") collapsed
 *      under the field — helps designers learn the brand vocabulary.
 *
 * Tests rely on the following selectors / labels — DO NOT rename without
 * updating tests:
 *   • Section titles: "Starter presets", "Logo", "Agent colors"
 *   • Field labels:   "Brand name", "Tagline", "Eyebrow", "Page title",
 *                     "Company name", "Company URL", "Primary",
 *                     "Primary hover", "Secondary", "Accent",
 *                     "Gradient (CSS)", "Icon"
 *   • Classes:        .wl-editor-header(-title|-sub), .wl-changes-badge,
 *                     .wl-section(-title|-subtitle|-body), .wl-field(-label|
 *                     -modified-dot), .wl-affects-chip, .wl-color-picker,
 *                     .wl-feature-row, .wl-font-preset, .wl-editor-preview,
 *                     .wl-editor-actions(-status)
 *   • Preview tabs:   "Login", "Home", "Chat" (Sidebar/Modal added)
 *   • Buttons:        "Save changes", "Discard", "Apply Brij preset",
 *                     "Apply M² preset"
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { TFunc } from '@/utils/tfunc';
import { bidiIsolate } from '@/utils/bidi';
import {
  Button, Drawer, Input, Select, message as antdMessage,
  Spin, Switch, Upload, Space, ColorPicker, Tooltip, Tag,
} from 'antd';
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { endPlacement } from '@/utils/rtl';
import type { UploadProps } from 'antd';
import { defaultBrand, setActiveBrand, brand as activeBrand } from '@/brand';
import { applyBrandCSS } from '@/brand/apply-brand';
import { mergeBrandOverrides } from '@/brand/merge-overrides';
import type { BrandConfig, BrandLoginFeature, BrandOverrides } from '@/brand/types';
import api from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage } from '@/hooks/useLanguage';
import {
  PreviewScope,
  PreviewTabs,
  PREVIEW_SURFACE_LABEL_KEY,
  PREVIEW_SURFACE_HINT_KEY,
  type PreviewSurface,
} from './components/PreviewSurfaces';
import FloatingPreviewWindow from './components/FloatingPreviewWindow';
import PresetGallery from './components/PresetGallery';
import AgentAvatarSets from './components/AgentAvatarSets';
import DesignSkillPanel from './components/DesignSkillPanel';
import EmailSendingSection from './components/EmailSendingSection';
import EmailSharingSection from './components/EmailSharingSection';
import SupportBoardSection, { type SupportBoardHandle, type SupportBoardState } from './components/SupportBoardSection';
import { createPreset } from './services/brandPresetsService';
import { getBrandSection, setBrandSection } from '@modules/super_admin/console/consoleState';
import './components/component-demo.css';
import './white-label-editor.css';
import DefaultUserSettingsPanel from '@modules/super_admin/components/DefaultUserSettingsPanel';
import AccessRow from '@modules/super_admin/components/AccessRow';

/* ─── Curated font catalog ────────────────────────────────────────────── */

interface FontPreset {
  id: string;
  label: string;
  description: string;
  /** True when every family in the preset ships Hebrew glyphs — the RTL
   *  filter in the catalog. A Hebrew-locale tenant picking a Latin-only
   *  family silently falls back to system fonts, which is exactly the
   *  trap the filter exists to prevent. */
  hebrew?: boolean;
  fonts: { heading: string; body: string; mono: string; googleFontsUrl: string };
}
const FONT_PRESETS: FontPreset[] = [
  {
    id: 'rounded',
    label: 'Rounded',
    description: 'Manrope + Plus Jakarta. Friendly, approachable.',
    fonts: {
      heading: 'Plus Jakarta Sans', body: 'Manrope', mono: 'JetBrains Mono',
      googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Manrope:wght@300;400;500;600;700&family=Plus+Jakarta+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap',
    },
  },
  {
    id: 'squared',
    label: 'Squared',
    description: 'Inter + IBM Plex Sans. Crisp, technical.',
    fonts: {
      heading: 'Inter', body: 'Inter', mono: 'IBM Plex Mono',
      googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=IBM+Plex+Sans:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap',
    },
  },
  {
    id: 'serif',
    label: 'Classic Serif',
    description: 'Source Serif + Source Sans. Editorial, trustworthy.',
    fonts: {
      heading: 'Source Serif 4', body: 'Source Sans 3', mono: 'JetBrains Mono',
      googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Source+Serif+4:wght@400;500;600;700&family=Source+Sans+3:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap',
    },
  },
  {
    id: 'display',
    label: 'Display',
    description: 'Fraunces (display) + Inter (body). High-contrast.',
    fonts: {
      heading: 'Fraunces', body: 'Inter', mono: 'JetBrains Mono',
      googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Fraunces:wght@400;500;600;700&family=Inter:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap',
    },
  },
  {
    id: 'grotesk',
    label: 'Grotesk',
    description: 'Space Grotesk + Inter. Contemporary, product-led.',
    fonts: {
      heading: 'Space Grotesk', body: 'Inter', mono: 'JetBrains Mono',
      googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Inter:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap',
    },
  },
  {
    id: 'humanist',
    label: 'Humanist',
    description: 'Nunito Sans throughout. Warm, readable.',
    fonts: {
      heading: 'Nunito Sans', body: 'Nunito Sans', mono: 'JetBrains Mono',
      googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@300;400;600;700&family=JetBrains+Mono:wght@400;500&display=swap',
    },
  },
  {
    id: 'elegant',
    label: 'Elegant',
    description: 'Playfair Display + Source Sans. Refined, upscale.',
    fonts: {
      heading: 'Playfair Display', body: 'Source Sans 3', mono: 'JetBrains Mono',
      googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=Source+Sans+3:wght@300;400;500;600&family=JetBrains+Mono:wght@400;500&display=swap',
    },
  },
  {
    id: 'hebrew-modern',
    label: 'Modern Hebrew',
    description: 'Rubik + Heebo. Full Hebrew coverage, geometric.',
    hebrew: true,
    fonts: {
      heading: 'Rubik', body: 'Heebo', mono: 'JetBrains Mono',
      googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Rubik:wght@400;500;600;700&family=Heebo:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap',
    },
  },
  {
    id: 'hebrew-clean',
    label: 'Clean Hebrew',
    description: 'Assistant throughout. Full Hebrew coverage, neutral.',
    hebrew: true,
    fonts: {
      heading: 'Assistant', body: 'Assistant', mono: 'JetBrains Mono',
      googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Assistant:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap',
    },
  },
  {
    id: 'hebrew-editorial',
    label: 'Editorial Hebrew',
    description: 'Frank Ruhl Libre + Assistant. Hebrew serif headlines.',
    hebrew: true,
    fonts: {
      heading: 'Frank Ruhl Libre', body: 'Assistant', mono: 'JetBrains Mono',
      googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Frank+Ruhl+Libre:wght@400;500;700&family=Assistant:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap',
    },
  },
];

/* ─── Editor groups (v2 shell) ───────────────────────────────────────── */

/** The rail of concerns. Each group owns one or more of the classic
 *  sections; only the active group renders, killing the 1,900-line scroll.
 *  Deep-linkable via URL hash (#colors) and remembered per session. */
export type EditorGroup =
  | 'basics' | 'colors' | 'typography' | 'assets' | 'login'
  | 'copy' | 'agents' | 'onboarding' | 'connectors' | 'defaults' | 'email' | 'support' | 'documents' | 'danger';

const EDITOR_GROUPS: Array<{ id: EditorGroup; labelKey: string; zone: 'brand' | 'surfaces' | 'outbound' | 'danger' }> = [
  { id: 'basics',     labelKey: 'superAdmin.wl.groupBasics',     zone: 'brand' },
  { id: 'colors',     labelKey: 'superAdmin.wl.groupColors',     zone: 'brand' },
  { id: 'typography', labelKey: 'superAdmin.wl.groupTypography', zone: 'brand' },
  { id: 'assets',     labelKey: 'superAdmin.wl.groupAssets',     zone: 'brand' },
  { id: 'login',      labelKey: 'superAdmin.wl.groupLogin',      zone: 'surfaces' },
  { id: 'copy',       labelKey: 'superAdmin.wl.groupCopy',       zone: 'surfaces' },
  { id: 'agents',     labelKey: 'superAdmin.wl.groupAgents',     zone: 'surfaces' },
  { id: 'onboarding', labelKey: 'superAdmin.wl.groupOnboarding', zone: 'surfaces' },
  { id: 'connectors', labelKey: 'superAdmin.wl.groupConnectors', zone: 'surfaces' },
  { id: 'defaults',   labelKey: 'superAdmin.wl.groupDefaults',   zone: 'surfaces' },
  { id: 'email',      labelKey: 'superAdmin.wl.groupEmail',      zone: 'outbound' },
  { id: 'support',    labelKey: 'superAdmin.wl.groupSupport',    zone: 'outbound' },
  { id: 'documents',  labelKey: 'superAdmin.wl.groupDocuments',  zone: 'outbound' },
  { id: 'danger',     labelKey: 'superAdmin.wl.groupDanger',     zone: 'danger' },
];

const ZONE_LABEL_KEY: Record<string, string> = {
  brand: 'superAdmin.wl.zoneBrand',
  surfaces: 'superAdmin.wl.zoneSurfaces',
  outbound: 'superAdmin.wl.zoneOutbound',
  danger: 'superAdmin.wl.zoneDanger',
};

/** Which top-level override keys light the modified dot on each rail item. */
const GROUP_KEYS: Record<EditorGroup, string[]> = {
  basics: ['name', 'appName', 'pageTitle', 'companyName', 'companyUrl'],
  colors: ['colors', 'lightMode', 'darkMode'],
  typography: ['fonts'],
  assets: ['logo', 'logoDark', 'icon', 'iconDark', 'favicon',
    // Preset-only marks (no upload in the editor yet), but "Apply preset"
    // writes them, so a stored value must light the dot like any asset.
    'conciergeIcon', 'conciergeIconDark',
    'anniversaryIcon', 'anniversaryIconDark', 'anniversaryLogo', 'anniversaryLogoDark'],
  // The tagline is edited on the Login page group (it is the login hero's
  // heading), so its unsaved change and its dot belong there.
  login: ['loginEyebrow', 'tagline', 'loginFeatures'],
  copy: ['terminology'],
  agents: ['agentColors', 'agentAvatars', 'homeSemicircle', 'chat'],
  // Onboarding is a FEATURE FLAG, not a brand override — it lives in
  // tenant_settings.features and saves the instant it is switched — so no
  // brand key here could ever light the rail dot.
  onboarding: [],
  // Same story for the Google Drive connector: a feature flag on the tenant
  // (integrations.cloud_storage), saved on toggle — no brand key to light.
  connectors: [],
  // Default user settings are NOT brand overrides - they live on the tenant
  // row and save on their own button - so no key here can light the rail dot.
  // The panel reports its own configured state through `defaultsConfigured`
  // instead, which is why the dot below is an OR of the two.
  defaults: [],
  email: ['email'],
  // Where support reports also go (a Monday board): its own table, saved on
  // its own button — no brand key to light the dot.
  support: [],
  documents: [],
  danger: [],
};

/* ─── WCAG contrast (built-in — no third-party checker needed) ────────── */

function hexToRgb(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function relativeLuminance(rgb: [number, number, number]): number {
  const [r, g, b] = rgb.map(v => {
    const c = v / 255;
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two hex colors; null when unparsable. */
export function contrastRatio(hexA: string, hexB: string): number | null {
  const a = hexToRgb(hexA);
  const b = hexToRgb(hexB);
  if (!a || !b) return null;
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

const colorTokenOptions = (t: TFunc) => ([
  { value: 'primary',   label: t('whiteLabel.colorToken.primary')   },
  { value: 'success',   label: t('whiteLabel.colorToken.success')   },
  { value: 'warning',   label: t('whiteLabel.colorToken.warning')   },
  { value: 'error',     label: t('whiteLabel.colorToken.error')     },
  { value: 'info',      label: t('whiteLabel.colorToken.info')      },
  { value: 'accent',    label: t('whiteLabel.colorToken.accent')    },
  { value: 'secondary', label: t('whiteLabel.colorToken.secondary') },
]);

/* ─── Field "Shows on" surface metadata ─────────────────────────────── */
//
// `tab` and `app` are pseudo-surfaces — describe places NOT in the preview
// pane (browser tab, every authenticated page) but worth calling out.
type Surface = PreviewSurface | 'tab' | 'app';
const surfaceLabel = (t: TFunc): Record<Surface, string> => ({
  // Object.fromEntries widens to Record<string, string>, which loses the
  // exhaustiveness the Surface union gives us, so map the keys explicitly.
  login: t(PREVIEW_SURFACE_LABEL_KEY.login),
  home: t(PREVIEW_SURFACE_LABEL_KEY.home),
  agents: t(PREVIEW_SURFACE_LABEL_KEY.agents),
  chat: t(PREVIEW_SURFACE_LABEL_KEY.chat),
  conversation: t(PREVIEW_SURFACE_LABEL_KEY.conversation),
  library: t(PREVIEW_SURFACE_LABEL_KEY.library),
  editAgent: t(PREVIEW_SURFACE_LABEL_KEY.editAgent),
  sidebar: t(PREVIEW_SURFACE_LABEL_KEY.sidebar),
  modal: t(PREVIEW_SURFACE_LABEL_KEY.modal),
  tab: t('whiteLabel.surface.browserTab'),
  app: t('whiteLabel.surface.acrossTheApp'),
});

/**
 * Internal view state for the editor — the editor itself, the inline
 * preset gallery, or the inline save-as-preset form.
 *
 * EXPORTED so the parent (DesignSystemPanel) can hoist it up and route
 * the page-level "Back" button: when the user is in `presets` or
 * `save-as`, Back returns to `edit`; when in `edit`, Back returns to
 * the parent's landing screen. Keeps a single Back button per page.
 */
export type EditorView = 'edit' | 'presets' | 'save-as';

interface Props {
  tenantId: string;
  /** Optional external control of internal view. When omitted the editor
   *  manages its own state (used by the standalone WhiteLabelEditorPage). */
  view?: EditorView;
  onViewChange?: (v: EditorView) => void;
}

/** The preview column's hidden state, remembered across visits. */
const PREVIEW_HIDDEN_KEY = 'wl.previewHidden';
function readPreviewHidden(): boolean {
  try { return localStorage.getItem(PREVIEW_HIDDEN_KEY) === '1'; } catch { return false; }
}

export default function WhiteLabelEditor({ tenantId, view: viewProp, onViewChange }: Props) {
  const { t, direction } = useLanguage();
  // The signed-in super-admin's own tenant. Used by `handleSave` to decide
  // whether to apply the saved overrides to the current session — editing
  // ANOTHER tenant should not re-skin (or cache!) the editor's own UI.
  const { tenantId: currentUserTenantId } = useAuth();
  // Tenant-level Google Drive permission LEVEL — two feature flags, saved on
  // toggle through the same endpoint the super-admin features panel uses:
  //   integrations.cloud_storage          level 1: read, search, save NEW files
  //   integrations.cloud_storage.editing  level 2: also rewrite existing files
  // The second is a child of the first (off when the connector is off) and
  // is the ONLY ceiling under which each agent's own "Edit files in Drive"
  // switch sits (operator decision 2026-09-25): on here means level 2 is on.
  // It also decides which Google scopes this tenant's users are asked for.
  const DRIVE_FLAG = 'integrations.cloud_storage';
  const DRIVE_EDIT_FLAG = 'integrations.cloud_storage.editing';
  const [driveConnectorOn, setDriveConnectorOn] = useState(true);
  const [driveEditingOn, setDriveEditingOn] = useState(false);
  const [driveConnectorSaving, setDriveConnectorSaving] = useState(false);
  const [driveConnectorLoaded, setDriveConnectorLoaded] = useState(false);
  const setDriveFlag = useCallback(async (flag: string, on: boolean) => {
    setDriveConnectorSaving(true);
    try {
      const res = await api.patch(`/super-admin/tenants/${tenantId}/features`, {
        features: { [flag]: on },
      });
      const resolved = res.data?.resolved || {};
      setDriveConnectorOn(resolved[DRIVE_FLAG] !== false);
      setDriveEditingOn(resolved[DRIVE_EDIT_FLAG] === true);
      if (tenantId === currentUserTenantId) {
        const { default: authService } = await import('@modules/auth/services/authService');
        await authService.verifyToken(true);
      }
    } catch (err: any) {
      antdMessage.error(err?.response?.data?.error || t('whiteLabel.connectors.changeFailed'));
    } finally {
      setDriveConnectorSaving(false);
    }
  }, [tenantId, currentUserTenantId]);
  // Developer access (MCP & API): whether people in this organization may
  // create access tokens and drive the platform from Claude Code, Codex or a
  // script. A kill switch, not a mint-time check — switching it off stops
  // every live token in the organization on the next request.
  const DEV_ACCESS_FLAG = 'modules.mcp_access';
  const [devAccessOn, setDevAccessOn] = useState<boolean | null>(null);
  const [devAccessSaving, setDevAccessSaving] = useState(false);
  const setDevAccess = useCallback(async (on: boolean) => {
    setDevAccessSaving(true);
    try {
      const res = await api.patch(`/super-admin/tenants/${tenantId}/features`, {
        features: { [DEV_ACCESS_FLAG]: on },
      });
      setDevAccessOn(res.data?.resolved?.[DEV_ACCESS_FLAG] === true);
      antdMessage.success(on ? t('whiteLabel.developerAccess.turnedOn') : t('whiteLabel.developerAccess.turnedOff'));
      if (tenantId === currentUserTenantId) {
        const { default: authService } = await import('@modules/auth/services/authService');
        await authService.verifyToken(true);
      }
    } catch (err: any) {
      antdMessage.error(err?.response?.data?.error || t('whiteLabel.connectors.changeFailed'));
    } finally {
      setDevAccessSaving(false);
    }
  }, [tenantId, currentUserTenantId]);
  const driveLevel: 'off' | 'save' | 'edit' =
    !driveConnectorOn ? 'off' : driveEditingOn ? 'edit' : 'save';
  const [overrides, setOverrides] = useState<BrandOverrides>({});
  const [savedSnapshot, setSavedSnapshot] = useState<BrandOverrides>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [applyingToSubtree, setApplyingToSubtree] = useState(false);

  // The onboarding journey is a CAPABILITY, not a brand token: it lives in
  // tenant_settings.features, not brand_overrides, and so it is written
  // through the features endpoint the moment it is switched rather than
  // riding along with Save. Kept beside the brand controls anyway because
  // this page is where a tenant is set up, and "does this tenant get the
  // tour" is part of setting one up.
  // null = not loaded yet.
  const [journeyOn, setJourneyOn] = useState<boolean | null>(null);
  const [journeySaving, setJourneySaving] = useState(false);

  /**
   * Turn the onboarding journey on or off for THIS tenant.
   *
   * Writes straight through instead of waiting for Save: the flag lives in
   * tenant_settings.features, a different store from brand_overrides, and
   * pretending one Save button owns both would mean a half-applied page
   * whenever one of the two requests failed. Optimistic, with a rollback,
   * so the switch never shows a state the server did not accept.
   */
  const setJourneyEnabled = useCallback(async (enabled: boolean) => {
    const previous = journeyOn;
    setJourneyOn(enabled);
    setJourneySaving(true);
    try {
      await api.patch(`/super-admin/tenants/${tenantId}/features`, {
        features: { 'modules.journey': enabled },
      });
      antdMessage.success(
        enabled
          ? t('whiteLabel.journey.turnedOn')
          : t('whiteLabel.journey.turnedOff'),
      );
    } catch (e: any) {
      setJourneyOn(previous);
      antdMessage.error(
        e?.response?.data?.error || t('whiteLabel.journey.changeFailed'),
      );
    } finally {
      setJourneySaving(false);
    }
  }, [tenantId, journeyOn]);

  // Active preview surface — shared between the docked rail AND the floating
  // window. Field focus and "Shows on" chip clicks update this.
  const [previewSurface, setPreviewSurface] = useState<PreviewSurface>('login');
  // Floating PIP. Default closed; rail is the visible preview by default.
  const [floatingOpen, setFloatingOpen] = useState(false);
  // Internal view state — per the "no popups" directive, the gallery and
  // the save-as-preset flow render INLINE in place of the editor body
  // (not as modals). When the parent supplies `view` + `onViewChange`
  // (DesignSystemPanel does), the parent's page-level Back button drives
  // navigation. Otherwise (standalone /super-admin/tenants/<id>/brand
  // page), the editor manages its own state.
  const [viewInternal, setViewInternal] = useState<EditorView>('edit');
  const view = viewProp ?? viewInternal;
  const setView = (v: EditorView) => {
    if (onViewChange) onViewChange(v);
    else setViewInternal(v);
  };
  // v2 shell — active rail group. Hash deep-link (#colors) wins, then the
  // session memory, then Basics. Persisted on every change so tenant
  // switches land the operator on the same section.
  // Whether this tenant has set any default user settings of its own. Owned by
  // DefaultUserSettingsPanel (they are tenant settings, not brand overrides, so
  // `overrides` cannot answer it) and reported back up so the rail can dot the
  // group the same way an override does.
  const [defaultsConfigured, setDefaultsConfigured] = useState(false);
  // Bumped by the Email sending section; the email preview re-reads its From.
  const [senderRevision, setSenderRevision] = useState(0);
  // White-Label → Support saves through the bottom bar like the brand fields:
  // the section reports its draft's state here and the bar calls it. It stays
  // mounted (hidden) once the editor is open, so switching groups never drops
  // an unsaved choice and the rail can dot it like an override.
  const supportRef = useRef<SupportBoardHandle>(null);
  const [support, setSupport] = useState<SupportBoardState>(
    { dirty: false, canSave: false, saving: false, configured: false, editable: false },
  );
  // The workspace (rail | form | preview) fills the height its host gives it;
  // each column scrolls on its own and the save footer never scrolls away.
  // Under 1100px the preview opens in a drawer; under 720px the rail is a
  // select and the page scrolls as one, with the footer pinned to its bottom.
  const narrow = useMediaQuery('(max-width: 1099px)');
  const compact = useMediaQuery('(max-width: 719px)');
  const [previewHidden, setPreviewHidden] = useState<boolean>(readPreviewHidden);
  const [previewDrawer, setPreviewDrawer] = useState(false);
  const togglePreviewHidden = () => {
    setPreviewHidden(hidden => {
      try { localStorage.setItem(PREVIEW_HIDDEN_KEY, hidden ? '0' : '1'); } catch { /* private mode */ }
      return !hidden;
    });
  };

  const [activeGroup, setActiveGroupState] = useState<EditorGroup>(() => {
    const fromHash = window.location.hash.replace('#', '');
    if (EDITOR_GROUPS.some(g => g.id === fromHash)) return fromHash as EditorGroup;
    const remembered = getBrandSection();
    if (EDITOR_GROUPS.some(g => g.id === remembered)) return remembered as EditorGroup;
    return 'basics';
  });
  const setActiveGroup = (g: EditorGroup) => {
    setActiveGroupState(g);
    setBrandSection(g);
    try { window.history.replaceState(null, '', `#${g}`); } catch { /* noop */ }
  };

  // The connector's current value is read only when its panel is actually
  // opened. Mounting the editor is not a reason to ask the features endpoint a
  // second time — the brand load at the bottom of this file already talks to
  // the server once.
  useEffect(() => {
    if (activeGroup !== 'connectors' || driveConnectorLoaded) return;
    let cancelled = false;
    api.get(`/super-admin/tenants/${tenantId}/features`)
      .then((res) => {
        if (cancelled) return;
        const resolved = res.data?.resolved || {};
        setDriveConnectorOn(resolved[DRIVE_FLAG] !== false);
        setDriveEditingOn(resolved[DRIVE_EDIT_FLAG] === true);
        setDevAccessOn(resolved[DEV_ACCESS_FLAG] === true);
        setDriveConnectorLoaded(true);
      })
      .catch(() => {
        // Leave the defaults; the switches still save. Developer access
        // defaults off in the registry, so show that rather than spin.
        if (!cancelled) setDevAccessOn(false);
      });
    return () => { cancelled = true; };
  }, [activeGroup, driveConnectorLoaded, tenantId]);
  // Hebrew-capable filter for the font catalog.
  const [fontFilter, setFontFilter] = useState<'all' | 'hebrew'>('all');

  const [saveAsName, setSaveAsName] = useState('');
  const [saveAsDescription, setSaveAsDescription] = useState('');
  const [savingAs, setSavingAs] = useState(false);

  // Pulse the preview after each change so the eye is drawn to the update.
  const [previewPulse, setPreviewPulse] = useState(false);
  const pulseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerPulse = useCallback(() => {
    setPreviewPulse(true);
    if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
    pulseTimerRef.current = setTimeout(() => setPreviewPulse(false), 500);
  }, []);

  // Single source of truth: tenant's brand_overrides JSONB merged onto
  // `defaultBrand`. There is no per-tenant "named brand" in the new model.
  const merged: BrandConfig = useMemo(
    () => mergeBrandOverrides(defaultBrand, overrides),
    [overrides],
  );

  // Load the merged Google Fonts URL into the editor's <head> so the preview
  // can actually render with the picked font BEFORE the user clicks Save.
  // Without this, picking "Squared" (Inter) shows no visible change because
  // the browser falls back to system-ui — and the designer thinks the editor
  // is broken. Tagged separately from apply-brand.ts's post-save link so we
  // don't fight that one across saves.
  useEffect(() => {
    const url = merged.fonts?.googleFontsUrl;
    if (!url) return;
    const TAG_KEY = 'data-brand-editor-fonts';
    let link = document.querySelector<HTMLLinkElement>(`link[${TAG_KEY}]`);
    if (!link) {
      link = document.createElement('link');
      link.rel = 'stylesheet';
      link.setAttribute(TAG_KEY, 'preview');
      document.head.appendChild(link);
    }
    if (link.href !== url) link.href = url;
    // Don't tear the link down on every overrides change — we re-use the
    // same node. The companion useEffect below handles unmount cleanup.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merged.fonts?.googleFontsUrl]);

  // Inject a second stylesheet scoped to .wl-preview-scope, .wl-type-sample,
  // and .wl-fpw that overrides the global !important rules apply-brand.ts
  // injects on h1-h6 / body+span+div. Without this, picking a different
  // font preset has zero visible effect on the preview because the saved
  // brand's font wins on cascade specificity (CSS !important > inline
  // style — yes, even inline). This stylesheet is appended AFTER apply-
  // brand's tag, so on equal !important specificity it wins on order.
  useEffect(() => {
    const heading = merged.fonts?.heading || 'Inter';
    const body = merged.fonts?.body || 'Inter';
    const mono = merged.fonts?.mono || 'JetBrains Mono';
    const c = merged.colors;
    const TAG_KEY = 'data-brand-editor-preview-scope';
    const css = `
      /* ─── Fonts ─── */
      /* Preview surfaces — universal selector + !important so EVERY element
         (including header/section/nav/ul/ol/td/th/blockquote/em/strong, plus
         Ant Design's many wrapper divs) reflects the form's body font. The
         per-tag list we used before missed a lot of tags and only some
         components updated, the rest stayed on the saved brand's font. */
      .wl-preview-scope,
      .wl-preview-scope * {
        font-family: '${body}', 'Ploni ML v2 AAA', 'Varela Round', -apple-system, BlinkMacSystemFont, sans-serif !important;
      }
      /* The exact stacks apply-brand.ts injects globally — anything else and
         the preview renders a different fallback than the real page does. */
      .wl-preview-scope h1, .wl-preview-scope h2, .wl-preview-scope h3,
      .wl-preview-scope h4, .wl-preview-scope h5, .wl-preview-scope h6,
      .wl-preview-scope [data-preview="heading"] {
        font-family: '${heading}', 'Jost', 'Ploni ML v2 AAA', 'Varela Round', -apple-system, BlinkMacSystemFont, sans-serif !important;
      }
      .wl-preview-scope code, .wl-preview-scope pre, .wl-preview-scope kbd,
      .wl-preview-scope samp, .wl-preview-scope [data-preview="mono"] {
        font-family: '${mono}', monospace !important;
      }
      /* Floating PIP — same rules. */
      .wl-fpw,
      .wl-fpw * {
        font-family: '${body}', 'Ploni ML v2 AAA', 'Varela Round', -apple-system, BlinkMacSystemFont, sans-serif !important;
      }
      .wl-fpw h1, .wl-fpw h2, .wl-fpw h3, .wl-fpw h4, .wl-fpw h5, .wl-fpw h6,
      .wl-fpw [data-preview="heading"] {
        font-family: '${heading}', 'Jost', 'Ploni ML v2 AAA', 'Varela Round', -apple-system, BlinkMacSystemFont, sans-serif !important;
      }
      .wl-fpw code, .wl-fpw pre, .wl-fpw kbd, .wl-fpw samp {
        font-family: '${mono}', monospace !important;
      }
      /* Type Sample — explicit per-row overrides via data-sample attribute. */
      .wl-type-sample [data-sample="heading"] {
        font-family: '${heading}', 'Ploni ML v2 AAA', 'Varela Round', -apple-system, BlinkMacSystemFont, sans-serif !important;
      }
      .wl-type-sample [data-sample="body"] {
        font-family: '${body}', 'Ploni ML v2 AAA', 'Varela Round', -apple-system, BlinkMacSystemFont, sans-serif !important;
      }
      .wl-type-sample [data-sample="mono"] {
        font-family: '${mono}', monospace !important;
      }

      /* ─── Colors ─── */
      /* The global apply-brand.ts injects a primary-button rule carrying the
         SAVED gradient with !important, which beats any inline style on the
         previewed buttons. These scoped rules counter it so the preview shows
         the FORM's draft colour instead of the saved one.

         The :not(:disabled) here is load-bearing, and not for the same
         reason as in apply-brand.ts. That global selector became
         ".ant-btn-primary:not(:disabled)" (0-2-0) when disabled buttons were
         fixed; this override was ".wl-preview-scope .ant-btn-primary", also
         0-2-0. Two !important rules of EQUAL specificity are resolved by
         stylesheet order alone — so whether the preview showed your draft or
         stayed stuck on the saved brand depended on which <style> happened to
         be injected last. Matching the pseudo-class here takes this to 0-3-0
         and wins outright, whatever the order. */
      .wl-preview-scope .ant-btn-primary:not(:disabled),
      .wl-fpw .ant-btn-primary:not(:disabled) {
        background: ${c.gradientPrimary} !important;
        border-color: ${c.primary} !important;
        color: #fff !important;
      }
      /* A disabled primary in the preview reads as "not yet" in the draft
         brand's own muted tones — mirroring what the app does — rather than
         inheriting the saved brand's disabled treatment. */
      .wl-preview-scope .ant-btn-primary:disabled,
      .wl-fpw .ant-btn-primary:disabled {
        background: ${c.primary}1f !important;
        border-color: ${c.primary}33 !important;
        color: ${c.primary}80 !important;
      }
      .wl-preview-scope .ant-btn-primary:not(:disabled):hover,
      .wl-fpw .ant-btn-primary:not(:disabled):hover {
        filter: brightness(0.95) !important;
        border-color: ${c.primaryHover} !important;
      }
      /* Links / focus rings — also locked by the global rule. */
      .wl-preview-scope a:not(.ant-btn),
      .wl-fpw a:not(.ant-btn) {
        color: ${c.primary} !important;
      }
      .wl-preview-scope .ant-input:focus,
      .wl-preview-scope .ant-input-affix-wrapper-focused,
      .wl-preview-scope .ant-input-focused,
      .wl-fpw .ant-input:focus,
      .wl-fpw .ant-input-affix-wrapper-focused,
      .wl-fpw .ant-input-focused {
        border-color: ${c.primary} !important;
        box-shadow: 0 0 0 2px ${c.primary}26 !important;
      }

    `;
    let styleTag = document.querySelector<HTMLStyleElement>(`style[${TAG_KEY}]`);
    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.setAttribute(TAG_KEY, 'true');
      document.head.appendChild(styleTag);
    }
    if (styleTag.textContent !== css) styleTag.textContent = css;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    merged.fonts?.heading, merged.fonts?.body, merged.fonts?.mono,
    merged.colors.primary, merged.colors.primaryHover,
    merged.colors.gradientPrimary,
  ]);

  // Cleanup the preview link + style tag when the whole editor unmounts.
  useEffect(() => () => {
    document.querySelector('link[data-brand-editor-fonts]')?.remove();
    document.querySelector('style[data-brand-editor-preview-scope]')?.remove();
  }, []);

  // Load saved overrides AND the tenant row (for tenant_domain +
  // subdomain_url) once per tenant. There's no GET /super-admin/tenants/<id>
  // endpoint (only PUT), so we pull the tenant row out of the list
  // endpoint — cheap enough since the list is already paginated to 1000.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [brandRes, featRes] = await Promise.all([
          api.get(`/super-admin/tenants/${tenantId}/brand-overrides`),
          // Resolved, not the raw override: with no override of its own a
          // tenant inherits, and the switch must show what the tenant
          // actually gets rather than what happens to be written on its row.
          api.get(`/super-admin/tenants/${tenantId}/features`).catch(() => null),
        ]);
        if (cancelled) return;
        setJourneyOn(Boolean(featRes?.data?.resolved?.['modules.journey']));
        const initial = (brandRes.data?.brand_overrides as BrandOverrides) || {};
        setOverrides(initial);
        setSavedSnapshot(initial);
      } catch (err: any) {
        // 404 = the tenant id we were handed doesn't exist in this backend
        // (typical cause: the dev/e2e database was switched and a stale
        // selection was left in localStorage). Treat it as "no overrides
        // yet" and surface a calmer hint, not the raw axios message.
        if (err?.response?.status === 404) {
          if (!cancelled) {
            setOverrides({});
            setSavedSnapshot({});
          }
          antdMessage.warning(
            t('whiteLabel.tenantNotFound'),
          );
        } else {
          antdMessage.error(t('whiteLabel.loadOverridesFailed', { detail: err?.message || err }));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tenantId]);

  const dirty = useMemo(
    () => JSON.stringify(overrides) !== JSON.stringify(savedSnapshot),
    [overrides, savedSnapshot],
  );

  function set<K extends keyof BrandOverrides>(key: K, value: BrandOverrides[K]) {
    setOverrides(prev => ({ ...prev, [key]: value }));
    triggerPulse();
  }
  function setColor(field: keyof NonNullable<BrandOverrides['colors']>, value: string) {
    setOverrides(prev => ({ ...prev, colors: { ...(prev.colors || {}), [field]: value } }));
    triggerPulse();
  }
  function setMode(mode: 'lightMode' | 'darkMode', field: keyof NonNullable<BrandOverrides['lightMode']>, value: string) {
    setOverrides(prev => ({ ...prev, [mode]: { ...(prev[mode] || {}), [field]: value } }));
    triggerPulse();
  }
  function setFont(field: keyof NonNullable<BrandOverrides['fonts']>, value: string) {
    setOverrides(prev => {
      const fonts = { ...(prev.fonts || {}) } as Record<string, string>;
      if (value) fonts[field as string] = value;
      else delete fonts[field as string];
      return { ...prev, fonts };
    });
    triggerPulse();
  }
  function applyFontPreset(preset: FontPreset) {
    setOverrides(prev => ({
      ...prev,
      fonts: { ...(prev.fonts || {}), ...preset.fonts },
    }));
    triggerPulse();
  }
  function setLoginFeatures(features: BrandLoginFeature[]) {
    setOverrides(prev => ({ ...prev, loginFeatures: features }));
    triggerPulse();
  }
  function setTerm(key: keyof NonNullable<BrandOverrides['terminology']>, value: string) {
    setOverrides(prev => ({ ...prev, terminology: { ...(prev.terminology || {}), [key]: value } }));
    triggerPulse();
  }
  function setEmail(key: keyof NonNullable<BrandOverrides['email']>, value: string) {
    setOverrides(prev => {
      const email = { ...(prev.email || {}) };
      if (value) {
        email[key] = value;
      } else {
        delete email[key]; // empty = unset → falls back to the general brand value
      }
      return { ...prev, email };
    });
    triggerPulse();
  }

  // Count of fields the user has changed since loading / last save.
  // Includes the out-of-band tenant_domain field — it's not in
  // brand_overrides but the diff badge counts it for parity.
  const changeCount = useMemo(() => {
    const a = overrides as Record<string, unknown>;
    const b = savedSnapshot as Record<string, unknown>;
    const allKeys = new Set<string>([...Object.keys(a), ...Object.keys(b)]);
    let n = 0;
    for (const k of allKeys) {
      if (JSON.stringify(a[k]) !== JSON.stringify(b[k])) n++;
    }
    return n;
  }, [overrides, savedSnapshot]);

  // List of changed top-level keys — feeds the diff hover-card on save bar.
  const changeKeys = useMemo(() => {
    const a = overrides as Record<string, unknown>;
    const b = savedSnapshot as Record<string, unknown>;
    const allKeys = new Set<string>([...Object.keys(a), ...Object.keys(b)]);
    const changed: string[] = [];
    for (const k of allKeys) {
      if (JSON.stringify(a[k]) !== JSON.stringify(b[k])) changed.push(k);
    }
    return changed;
  }, [overrides, savedSnapshot]);

  // The rail groups whose brand fields hold the unsaved changes, in rail order.
  const unsavedGroups = useMemo(
    () => EDITOR_GROUPS.filter(g => GROUP_KEYS[g.id].some(k => changeKeys.includes(k))).map(g => g.id),
    [changeKeys],
  );

  function isFieldModified(path: string): boolean {
    const get = (obj: any, parts: string[]): unknown => {
      let cur = obj;
      for (const p of parts) cur = cur?.[p];
      return cur;
    };
    const parts = path.split('.');
    return JSON.stringify(get(overrides, parts)) !== JSON.stringify(get(savedSnapshot, parts));
  }

  // When a field gets focus, switch the preview to its primary surface so
  // the user sees the change land. Skips the pseudo-surfaces `tab` / `app`.
  function previewFor(affects: Surface[]) {
    if (!affects.length) return;
    const first = affects[0];
    if (first === 'tab' || first === 'app') return;
    if (first !== previewSurface) {
      setPreviewSurface(first);
      // Switching surfaces should also drop stack mode so the user sees the
      // single surface they're focused on.
    }
  }

  async function handleSave() {
    console.log('[brand-save] start', { tenantId, currentUserTenantId, overridesKeys: Object.keys(overrides) });
    setSaving(true);
    try {
      // Brand-overrides PATCH first — it's the bulk of the diff and the
      // happy-path operation. tenant_domain goes through a separate
      // endpoint (PUT /tenants/<id>) because it's a top-level column,
      // not part of the JSONB blob.
      if (JSON.stringify(overrides) !== JSON.stringify(savedSnapshot)) {
        // 20s hard timeout so a stuck connection still releases the
        // "Saving…" spinner — without this an unreachable backend can
        // leave the button in loading state indefinitely (axios's own
        // timeout was not always firing on Cloud Run cold starts).
        const res = await api.patch(
          `/super-admin/tenants/${tenantId}/brand-overrides`,
          overrides,
          { timeout: 20_000 },
        );
        console.log('[brand-save] PATCH ok', res.status);
        const saved = res.data?.brand_overrides || overrides;
        setSavedSnapshot(saved);
        // Apply the saved overrides to the global brand ONLY when the
        // tenant being edited IS the super-admin's own tenant. Otherwise
        // the act of saving another tenant's brand re-skins (and
        // localStorage-caches!) the editor's own session — a super-admin
        // editing `team-alpha` from a `corpbrain-admin` login should see
        // their corpbrain-admin UI stay corpbrain-admin.
        const editingOwnTenant = !!currentUserTenantId && tenantId === currentUserTenantId;
        if (editingOwnTenant) {
          // Wrap the apply step so a throw HERE (e.g. a stale brand
          // listener) doesn't leak past the outer catch — the request
          // already succeeded; the UI update failure is non-fatal.
          try {
            setActiveBrand(Object.keys(saved).length > 0 ? saved : null);
            applyBrandCSS(activeBrand);
          } catch (applyErr) {
            console.warn('[brand-save] applyBrandCSS failed (save itself succeeded):', applyErr);
          }
        }
      }
      antdMessage.success(t('whiteLabel.brandChangesSaved'));
    } catch (err: any) {
      // Surface the per-field Pydantic errors instead of just the generic
      // top-level "Validation failed". Without this, the user sees no clue
      // about which override key the backend rejected — and on a deployed
      // backend that's behind the frontend schema, the offending field
      // (e.g. a `homeSemicircle` block sent against an older schema that
      // doesn't know it) is the only useful diagnostic.
      const data = err?.response?.data;
      const fieldErrors = Array.isArray(data?.validation_errors)
        ? data.validation_errors
            .map((e: any) => `${e.field ?? '?'}: ${e.message ?? 'invalid'}`)
            .join('; ')
        : '';
      const detail = fieldErrors || data?.error || err?.message || String(err);
      antdMessage.error(`Save failed: ${detail}`);
      console.error('[brand-save] backend response:', data ?? err);
    } finally {
      setSaving(false);
    }
  }
  function handleDiscard() {
    setOverrides(savedSnapshot);
  }
  async function handleReset() {
    setSaving(true);
    try {
      await api.patch(`/super-admin/tenants/${tenantId}/brand-overrides`, {});
      setOverrides({});
      setSavedSnapshot({});
      antdMessage.success(t('whiteLabel.resetToBase'));
    } catch (err: any) {
      antdMessage.error(t('whiteLabel.resetFailed', { detail: err?.message || err }));
    } finally {
      setSaving(false);
    }
  }

  // Push this tenant's SAVED brand down the whole subtree, so rolling a
  // preset out to a company no longer means editing each child by hand.
  // Deliberately propagates what is persisted rather than the in-form
  // draft — hence the `disabled={dirty}` guard on the button — so what the
  // children get is exactly what this tenant demonstrably has.
  async function handleApplyToDescendants() {
    setApplyingToSubtree(true);
    try {
      const probe = await api.post(
        `/super-admin/tenants/${tenantId}/brand-overrides/apply-to-descendants`,
        { dry_run: true },
      );
      const count: number = probe.data.count;
      if (count === 0) {
        antdMessage.info(t('whiteLabel.noSubTenants'));
        return;
      }
      const names = (probe.data.targets || [])
        .map((t: { tenant_name: string }) => t.tenant_name).join(', ');
      // Overwrites each child's own brand — make that explicit before writing.
      if (!window.confirm(
        t('counts.applyBrandConfirm', { count }) + '\n\n'
        + `${names}\n\n` + t('whiteLabel.applyBrandReplaces')
      )) return;

      const res = await api.post(
        `/super-admin/tenants/${tenantId}/brand-overrides/apply-to-descendants`, {},
      );
      antdMessage.success(
        t('counts.brandAppliedToSubTenants', { count: res.data.count }),
      );
    } catch (err: any) {
      antdMessage.error(
        `Apply to sub-tenants failed: ${err?.response?.data?.error || err?.message || err}`,
      );
    } finally {
      setApplyingToSubtree(false);
    }
  }

  // Asset upload factory — produces an `UploadProps` for the wide login
  // wordmark (`field: 'logo'`), the square in-app mark (`field: 'icon'`),
  // or the browser-tab favicon (`field: 'favicon'`). All three target
  // POST /super-admin/tenants/<id>/brand-logo, which writes to
  // tenant_settings.brand_overrides[<field>].
  const makeUploadProps = (field: 'logo' | 'icon' | 'favicon', dark = false): UploadProps => ({
    accept: 'image/svg+xml,image/png,image/jpeg,image/x-icon,image/vnd.microsoft.icon',
    showUploadList: false,
    beforeUpload: (file) => {
      const reader = new FileReader();
      reader.onload = async () => {
        const dataUrl = reader.result as string;
        const labelMap = { logo: 'Logo', icon: 'Icon', favicon: 'Favicon' } as const;
        if (dataUrl.length > 200_000) {
          antdMessage.error(`${labelMap[field]} exceeds 200KB. Optimize the SVG/PNG and try again.`);
          return;
        }
        // Dark variants land on `<field>Dark` (backend writes the same key).
        const targetKey = (dark ? `${field}Dark` : field) as keyof BrandOverrides;
        try {
          await api.post(`/super-admin/tenants/${tenantId}/brand-logo`, {
            data_url: dataUrl, field, dark,
          });
          setOverrides(prev => ({ ...prev, [targetKey]: dataUrl }));
          setSavedSnapshot(prev => ({ ...prev, [targetKey]: dataUrl }));
          antdMessage.success(`Uploaded (${Math.round(dataUrl.length / 1024)}KB)`);
        } catch (err: any) {
          antdMessage.error(`Upload failed: ${err?.message || err}`);
        }
      };
      reader.readAsDataURL(file);
      return false; // we handle the upload manually
    },
  });

  const logoUploadProps = makeUploadProps('logo');
  const iconUploadProps = makeUploadProps('icon');
  const faviconUploadProps = makeUploadProps('favicon');

  // Ctrl/Cmd+S saves what the footer's Save would save on this group. The
  // ref keeps the listener stable while reading the current state.
  const saveShortcut = useRef<() => void>(() => {});
  saveShortcut.current = () => {
    if (activeGroup === 'support') {
      if (support.canSave) void supportRef.current?.save();
      return;
    }
    if (activeGroup !== 'defaults' && dirty && !saving) void handleSave();
  };
  useEffect(() => {
    if (view !== 'edit') return undefined;
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 's') {
        e.preventDefault();
        saveShortcut.current();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [view]);

  if (loading) return <Spin />;

  const onChipClick = (s: Surface) => {
    if (s === 'tab' || s === 'app') return;
    setPreviewSurface(s);
  };

  // Header strip for the 'presets' and 'save-as' sub-views.
  // No back button here — the parent's page-level Back button handles
  // navigation (DesignSystemPanel.handleEditingBack steps the view back
  // one level: sub-step → editor → landing). Single Back per page.
  const SubStepHeader = ({ title, sub }: { title: string; sub?: string }) => (
    <header className="wl-substep-header">
      <h2 className="wl-substep-title">{title}</h2>
      {sub && <div className="wl-substep-sub">{sub}</div>}
    </header>
  );

  const groupLabel = (id: EditorGroup) => t(EDITOR_GROUPS.find(g => g.id === id)?.labelKey ?? '');
  const showPreviewColumn = !narrow && !previewHidden;
  // Phones: the footer's buttons are small so it stays one row.
  const footerSize = compact ? 'small' as const : undefined;
  // The footer offers the preview only where it is not on screen: narrow →
  // open it in a drawer; wide with the column hidden → show it again. Hiding
  // it is the preview header's own button, so the footer stays one row.
  const previewToggle = narrow ? (
    <Button size={footerSize} icon={<EyeOutlined />} onClick={() => setPreviewDrawer(true)} data-testid="wl-preview-toggle">
      {t('whiteLabel.workspace.preview')}
    </Button>
  ) : previewHidden ? (
    <Button size={footerSize} type="text" icon={<EyeOutlined />} onClick={togglePreviewHidden} data-testid="wl-preview-toggle">
      {t('whiteLabel.workspace.showPreview')}
    </Button>
  ) : null;

  const previewBody = (<>
    <header className="wl-preview-header">
      <div className="wl-preview-header-row">
        <div>
          <div className="wl-preview-header-title">{t('whiteLabel.livePreview')}</div>
          <div className="wl-preview-header-sub">
            {t(PREVIEW_SURFACE_HINT_KEY[previewSurface])}
          </div>
        </div>
        <Space size={6}>
          {/* Save-status badge inline with Pop out so it shares the
              action row instead of stacking above the header (which
              shifted the preview surface down). */}
          {dirty
            ? <span className="wl-changes-badge"><span className="wl-changes-dot" /> {t('common.nUnsaved', { count: changeCount })}</span>
            : <span className="wl-changes-badge wl-changes-badge--clean">{t('whiteLabel.allSaved')}</span>}
          <Tooltip title={t('whiteLabel.popThePreviewIntoA')}>
            <Button
              size="small"
              type={floatingOpen ? 'primary' : 'default'}
              onClick={() => setFloatingOpen(o => !o)}
            >
              {floatingOpen ? t('whiteLabel.dock') : t('whiteLabel.popOut')}
            </Button>
          </Tooltip>
          {!narrow && (
            <Tooltip title={t('whiteLabel.workspace.hidePreview')}>
              <Button size="small" type="text" icon={<EyeInvisibleOutlined />}
                      aria-label={t('whiteLabel.workspace.hidePreview')}
                      onClick={togglePreviewHidden} data-testid="wl-preview-hide" />
            </Tooltip>
          )}
        </Space>
      </div>
    </header>

    {/* TypeSample was previously pinned above the preview surface as
        an always-visible font sample. Removed in favour of more
        vertical room for the actual preview — fonts are still
        obvious in the rendered surfaces below (Login tagline, agent
        names, body text). */}
    <PreviewScope merged={merged}>
      <div className={previewPulse ? 'wl-preview-pulse' : ''}>
        <PreviewTabs merged={merged} active={previewSurface} onChange={setPreviewSurface} />
      </div>
    </PreviewScope>
  </>);

  return (
    <>
      {view === 'edit' && (
      <div className={`wl-editor wl-editor--v2${showPreviewColumn ? '' : ' wl-editor--no-preview'}`}
           data-testid="wl-workspace">
        {/* ─── Rail of concerns (v2) — pick a group, see only it ── */}
        {compact ? (
          /* Phones: the rail is a select at the top, pinned while the page scrolls. */
          <div className="wl-rail-select">
            <label className="wl-rail-select-label" htmlFor="wl-group-select">{t('superAdmin.wl.railLabel')}</label>
            <select id="wl-group-select" className="wl-rail-select-control" value={activeGroup}
                    onChange={e => setActiveGroup(e.target.value as EditorGroup)} data-testid="wl-group-select">
              {(['brand', 'surfaces', 'outbound', 'danger'] as const).map(zone => (
                <optgroup key={zone} label={t(ZONE_LABEL_KEY[zone])}>
                  {EDITOR_GROUPS.filter(g => g.zone === zone).map(g => (
                    <option key={g.id} value={g.id}>
                      {unsavedGroups.includes(g.id) || (g.id === 'support' && support.dirty)
                        ? t('whiteLabel.workspace.groupUnsaved', { group: t(g.labelKey) })
                        : t(g.labelKey)}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
        ) : (
        <nav className="wl-rail" aria-label={t('superAdmin.wl.railLabel')}>
          {(['brand', 'surfaces', 'outbound', 'danger'] as const).map(zone => (
            <div key={zone} className="wl-rail-zone">
              <div className="wl-rail-zone-label">{t(ZONE_LABEL_KEY[zone])}</div>
              {EDITOR_GROUPS.filter(g => g.zone === zone).map(g => {
                // Dot = this tenant diverges from the base brand here
                // (saved or not) — scanning the rail shows the shape of
                // the tenant's brand in one second.
                const hasOverride = GROUP_KEYS[g.id].some(
                  k => k in (overrides as Record<string, unknown>))
                  || (g.id === 'defaults' && defaultsConfigured)
                  || (g.id === 'support' && support.configured);
                const unsaved = unsavedGroups.includes(g.id) || (g.id === 'support' && support.dirty);
                return (
                  <button
                    key={g.id}
                    type="button"
                    className={`wl-rail-item ${activeGroup === g.id ? 'wl-rail-item--active' : ''} ${g.zone === 'danger' ? 'wl-rail-item--danger' : ''}`}
                    aria-current={activeGroup === g.id ? 'true' : undefined}
                    onClick={() => setActiveGroup(g.id)}
                    data-testid={`wl-group-${g.id}`}
                  >
                    <span className="wl-rail-item-label">{t(g.labelKey)}</span>
                    {unsaved && (
                      <span className="wl-rail-unsaved" title={t('whiteLabel.workspace.unsavedMark')}>
                        <span className="wl-sr-only">{t('whiteLabel.workspace.unsavedMark')}</span>
                      </span>
                    )}
                    {hasOverride && !unsaved && <span className="wl-rail-dot" aria-hidden />}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>
        )}

        {/* ─── Form column — only the active group renders ──── */}
        <div className="wl-editor-form">
          <div className="wl-form-scroll" data-testid="wl-form-scroll">
          {/* Editor explanation header removed per user request — the
              sticky page header above ("Design System / Designing brand
              for <Tenant>") already provides context, and the save-status
              badge now lives on the preview rail. */}

          {activeGroup === 'basics' && (<>
          {/* ── Starter presets ──────────────────────────────
              Single primary CTA opens the preset gallery (where the user
              can preview + apply ANY preset, built-in or user-created).
              The previous quick-apply per-preset buttons were noisy;
              gallery is the canonical entry point. */}
          <Section
            title={t('whiteLabel.starterPresets')}
            subtitle={t('whiteLabel.presetsSubtitle')}
            why={t('whiteLabel.whyPresets')}
          >
            <div className="wl-presets-cards">
              <button
                type="button"
                className="wl-presets-card wl-presets-card--primary"
                onClick={() => setView('presets')}
                data-testid="open-preset-gallery"
              >
                <span className="wl-presets-card-title">{t('whiteLabel.browsePresets')}</span>
                <span className="wl-presets-card-sub">
                  {t('superAdmin.pickABrandTemplateM')}
                </span>
                <span className="wl-presets-card-cta">{t('whiteLabel.openGallery')}</span>
              </button>
              <button
                type="button"
                className="wl-presets-card"
                onClick={() => { setSaveAsName(''); setSaveAsDescription(''); setView('save-as'); }}
                data-testid="open-save-as-preset"
              >
                <span className="wl-presets-card-title">{t('whiteLabel.saveCurrentAsPreset')}</span>
                <span className="wl-presets-card-sub">
                  {t('superAdmin.captureTheFormSCurrent')}
                </span>
                <span className="wl-presets-card-cta">{t('whiteLabel.savePresetCta')}</span>
              </button>
            </div>
          </Section>

          {/* ── Identity ────────────────────────────────────── */}
          <Section
            title={t('whiteLabel.identity')}
            subtitle={t('whiteLabel.identitySubtitle')}
            why={t('whiteLabel.whyIdentity')}
          >
            <Field
              label={t('whiteLabel.brandName')}
              hint={t('whiteLabel.shortNameShownAsLogo')}
              affects={['login', 'home', 'sidebar', 'app']}
              modified={isFieldModified('name')}
              onFocus={() => previewFor(['sidebar', 'login'])}
              onChipClick={onChipClick}
            >
              <Input dir="auto" value={overrides.name ?? ''} placeholder={defaultBrand.name} onChange={e => set('name', e.target.value)} />
            </Field>
            <Field
              label={t('whiteLabel.appName')}
              hint={t('whiteLabel.appNameHint')}
              modified={isFieldModified('appName')}
              onChipClick={onChipClick}
              whyChange={t('whiteLabel.whyChangeAppName')}
            >
              <Input dir="auto" value={overrides.appName ?? ''} placeholder={t('whiteLabel.appNamePlaceholder')} onChange={e => set('appName', e.target.value)} />
            </Field>
            <Field
              label={t('whiteLabel.pageTitle')}
              hint={t('whiteLabel.browserTabTitleShownIn')}
              affects={['tab']}
              modified={isFieldModified('pageTitle')}
              onFocus={() => previewFor(['login'])}
              onChipClick={onChipClick}
              whyChange={t('whiteLabel.whyChangePageTitle')}
            >
              <Input dir="auto" value={overrides.pageTitle ?? ''} placeholder={defaultBrand.pageTitle} onChange={e => set('pageTitle', e.target.value)} />
            </Field>
            <Field
              label={t('whiteLabel.companyName')}
              hint={t('whiteLabel.brandMetadataUsedInThe')}
              affects={['sidebar', 'app']}
              modified={isFieldModified('companyName')}
              onFocus={() => previewFor(['sidebar'])}
              onChipClick={onChipClick}
            >
              <Input dir="auto" value={overrides.companyName ?? ''} placeholder={defaultBrand.companyName} onChange={e => set('companyName', e.target.value)} />
            </Field>
            <Field
              label={t('whiteLabel.companyUrl')}
              hint={t('whiteLabel.theLinkTargetWhenA')}
              affects={['login']}
              modified={isFieldModified('companyUrl')}
              onFocus={() => previewFor(['login'])}
              onChipClick={onChipClick}
            >
              <Input dir="auto" value={overrides.companyUrl ?? ''} placeholder={defaultBrand.companyUrl} onChange={e => set('companyUrl', e.target.value)} />
            </Field>
            {/* The sign-in hostname moved to the workspace's Access & login
                tab. It decides which URL resolves to this tenant — an access
                setting, not a brand one — and it writes a column on the
                tenant row rather than a brand override, so it never belonged
                on this save bar. */}
          </Section>

          </>)}

          {activeGroup === 'login' && (<>
          {/* ── Login surface ───────────────────────────────── */}
          <Section
            title={t('whiteLabel.loginSurface')}
            subtitle={t('whiteLabel.loginSubtitle')}
            why={t('whiteLabel.whyLogin')}
          >
            <Field
              label={t('whiteLabel.eyebrow')}
              hint={t('whiteLabel.smallLabelAboveTheTagline')}
              affects={['login']}
              modified={isFieldModified('loginEyebrow')}
              onFocus={() => previewFor(['login'])}
              onChipClick={onChipClick}
              whyChange={t('whiteLabel.whyChangeEyebrow')}
            >
              <Input dir="auto" value={overrides.loginEyebrow ?? ''} placeholder={defaultBrand.loginEyebrow ?? defaultBrand.name} onChange={e => set('loginEyebrow', e.target.value)} />
            </Field>
            <Field
              label={t('whiteLabel.tagline')}
              hint={t('whiteLabel.largeHeadingUnderTheEyebrow')}
              affects={['login']}
              modified={isFieldModified('tagline')}
              onFocus={() => previewFor(['login'])}
              onChipClick={onChipClick}
            >
              <Input dir="auto" value={overrides.tagline ?? ''}
                placeholder={t(defaultBrand.tagline, { defaultValue: defaultBrand.tagline })}
                onChange={e => set('tagline', e.target.value)} />
            </Field>
            <Field
              label={t('whiteLabel.featureTicks')}
              hint={t('whiteLabel.upTo8BulletsOn')}
              affects={['login']}
              modified={isFieldModified('loginFeatures')}
              onFocus={() => previewFor(['login'])}
              onChipClick={onChipClick}
            >
              <FeatureBulletEditor
                value={overrides.loginFeatures ?? defaultBrand.loginFeatures ?? []}
                onChange={setLoginFeatures}
              />
            </Field>
          </Section>

          </>)}

          {activeGroup === 'copy' && (<>
          {/* ── Greetings & copy ────────────────────────────── */}
          {/* Per-tenant phrasing layered on top of i18n. One dictionary serves
              every language — a custom phrase shows in both EN and HE. Blank
              fields fall back to the normal translation. */}
          <Section
            title={t('whiteLabel.greetingsCopy')}
            subtitle={t('whiteLabel.greetingsSubtitle')}
            why={t('whiteLabel.whyGreeting', { token: '{{name}}' })}
          >
            <Field
              label={t('whiteLabel.greeting')}
              hint={t('whiteLabel.homePageHeroLineUse', { token: '{{name}}' })}
              affects={['home']}
              modified={isFieldModified('terminology.greeting')}
              onFocus={() => previewFor(['home'])}
              onChipClick={onChipClick}
            >
              <Input dir="auto"
                value={overrides.terminology?.greeting ?? ''}
                placeholder={t('whiteLabel.shalom', { token: '{{name}}' })}
                onChange={e => setTerm('greeting', e.target.value)}
              />
            </Field>
            <Field
              label={t('whiteLabel.greetingSubLine')}
              hint={t('whiteLabel.theSmallerLineShownUnder')}
              affects={['home']}
              modified={isFieldModified('terminology.greetingQuestion')}
              onFocus={() => previewFor(['home'])}
              onChipClick={onChipClick}
            >
              <Input dir="auto"
                value={overrides.terminology?.greetingQuestion ?? ''}
                placeholder={t('whiteLabel.howCanIHelpYou')}
                onChange={e => setTerm('greetingQuestion', e.target.value)}
              />
            </Field>
          </Section>

          </>)}

          {activeGroup === 'colors' && (<>
          <ContrastReadout merged={merged} />
          {/* ── Colors ──────────────────────────────────────── */}
          <Section
            title={t('whiteLabel.colors')}
            subtitle={t('whiteLabel.colorsSubtitle')}
            why={t('whiteLabel.whyColors')}
          >
            <Field
              label={t('whiteLabel.primary')}
              hint={t('whiteLabel.theHeadlineBrandColorUsed')}
              affects={['app', 'home', 'chat', 'sidebar', 'login']}
              modified={isFieldModified('colors.primary')}
              onFocus={() => previewFor(['home'])}
              onChipClick={onChipClick}
              whyChange={t('whiteLabel.whyChangePrimary')}
            >
              <ColorPickerInput value={overrides.colors?.primary ?? defaultBrand.colors.primary} onChange={v => setColor('primary', v)} />
            </Field>
            <Field
              label={t('whiteLabel.primaryHover')}
              hint={t('whiteLabel.theHoverActiveVariantOf')}
              affects={['app']}
              modified={isFieldModified('colors.primaryHover')}
              onFocus={() => previewFor(['home'])}
              onChipClick={onChipClick}
            >
              <ColorPickerInput value={overrides.colors?.primaryHover ?? defaultBrand.colors.primaryHover} onChange={v => setColor('primaryHover', v)} />
            </Field>
            <Field
              label={t('whiteLabel.secondary')}
              hint={t('whiteLabel.aComplementaryColorForAccents')}
              affects={['app']}
              modified={isFieldModified('colors.secondary')}
              onFocus={() => previewFor(['home'])}
              onChipClick={onChipClick}
            >
              <ColorPickerInput value={overrides.colors?.secondary ?? defaultBrand.colors.secondary} onChange={v => setColor('secondary', v)} />
            </Field>
            <Field
              label={t('whiteLabel.accent')}
              hint={t('whiteLabel.aDecorativeThirdColorTypically')}
              affects={['login', 'app']}
              modified={isFieldModified('colors.accent')}
              onFocus={() => previewFor(['login'])}
              onChipClick={onChipClick}
            >
              <ColorPickerInput value={overrides.colors?.accent ?? defaultBrand.colors.accent} onChange={v => setColor('accent', v)} />
            </Field>
            <Field
              label={t('whiteLabel.gradientCss')}
              hint={t('whiteLabel.gradientCssHint')}
              affects={['login', 'home', 'chat']}
              modified={isFieldModified('colors.gradientPrimary')}
              onFocus={() => previewFor(['login'])}
              onChipClick={onChipClick}
              whyChange={t('whiteLabel.whyChangeGradient')}
            >
              <Input
                // Show the gradient that is actually in force. When the tenant
                // has not written one, that is the value DERIVED from their
                // Primary — not the platform default. Falling back to the base
                // brand here printed the stock blue while every button and coin
                // on the same screen already rendered the tenant's own colour.
                value={overrides.colors?.gradientPrimary ?? merged.colors.gradientPrimary}
                onChange={e => setColor('gradientPrimary', e.target.value)}
                style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}
              />
            </Field>
          </Section>

          <Section
            title={t('superAdmin.wl.secShadesTitle')}
            subtitle={t('superAdmin.wl.secShadesSub')}
          >
            <Field
              label={t('superAdmin.wl.secLight')}
              affects={['app']}
              modified={isFieldModified('colors.secondaryLight')}
              onChipClick={onChipClick}
            >
              <ColorPickerInput value={merged.colors.secondaryLight} onChange={v => setColor('secondaryLight', v)} />
            </Field>
            <Field
              label={t('superAdmin.wl.secDark')}
              affects={['app']}
              modified={isFieldModified('colors.secondaryDark')}
              onChipClick={onChipClick}
            >
              <ColorPickerInput value={merged.colors.secondaryDark} onChange={v => setColor('secondaryDark', v)} />
            </Field>
          </Section>

          <Section
            title={t('superAdmin.wl.surfacesTitle')}
            subtitle={t('superAdmin.wl.surfacesSub')}
          >
            <Field
              label={t('superAdmin.wl.lightSurfaces')}
              affects={['app', 'home']}
              modified={isFieldModified('lightMode')}
              onChipClick={onChipClick}
            >
              <div className="wl-surface-grid">
                {(['bgPrimary', 'bgSecondary', 'bgTertiary', 'bgElevated'] as const).map(k => (
                  <div key={k} className="wl-surface-cell">
                    <span className="wl-surface-cell-label">{k.replace('bg', '')}</span>
                    <ColorPickerInput
                      value={merged.lightMode?.[k] ?? '#ffffff'}
                      onChange={v => setMode('lightMode', k, v)}
                    />
                  </div>
                ))}
              </div>
            </Field>
            <Field
              label={t('superAdmin.wl.darkSurfaces')}
              affects={['app', 'home']}
              modified={isFieldModified('darkMode')}
              onChipClick={onChipClick}
            >
              <div className="wl-surface-grid">
                {(['bgPrimary', 'bgSecondary', 'bgTertiary', 'bgElevated'] as const).map(k => (
                  <div key={k} className="wl-surface-cell">
                    <span className="wl-surface-cell-label">{k.replace('bg', '')}</span>
                    <ColorPickerInput
                      value={merged.darkMode?.[k] ?? '#111827'}
                      onChange={v => setMode('darkMode', k, v)}
                    />
                  </div>
                ))}
              </div>
            </Field>
          </Section>

          </>)}

          {activeGroup === 'typography' && (<>
          {/* ── Typography ──────────────────────────────────── */}
          <Section
            title={t('whiteLabel.typography')}
            subtitle={t('whiteLabel.typographySubtitle')}
            why={t('whiteLabel.whyTypography')}
          >
            <Field
              label={t('whiteLabel.fontPreset')}
              hint={t('whiteLabel.fontPresetHint')}
              affects={['app', 'login', 'home', 'chat']}
              modified={isFieldModified('fonts')}
              onFocus={() => previewFor(['home'])}
              onChipClick={onChipClick}
              whyChange={t('whiteLabel.whyChangeFonts')}
            >
              <div className="wl-font-filter" role="group" aria-label={t('superAdmin.wl.fontFilterLabel')}>
                <button
                  type="button"
                  className={`wl-font-filter-chip ${fontFilter === 'all' ? 'wl-font-filter-chip--active' : ''}`}
                  onClick={() => setFontFilter('all')}
                >
                  {t('superAdmin.wl.fontFilterAll')}
                </button>
                <button
                  type="button"
                  className={`wl-font-filter-chip ${fontFilter === 'hebrew' ? 'wl-font-filter-chip--active' : ''}`}
                  onClick={() => setFontFilter('hebrew')}
                  data-testid="wl-font-filter-hebrew"
                >
                  {t('superAdmin.wl.fontFilterHebrew')}
                </button>
              </div>
              <div className="wl-font-presets">
                {FONT_PRESETS.filter(p => fontFilter === 'all' || p.hebrew).map(preset => {
                  // Active state must compare the FULL triplet (heading +
                  // body + mono). Squared and Display both have body=Inter,
                  // so `body`-only matching lit up both — the second one
                  // visually "won", making it look like clicking Squared
                  // selected Display.
                  const f = overrides.fonts;
                  const isActive =
                    f?.heading === preset.fonts.heading &&
                    f?.body === preset.fonts.body &&
                    f?.mono === preset.fonts.mono;
                  return (
                    <button
                      key={preset.id}
                      type="button"
                      className={`wl-font-preset ${isActive ? 'wl-font-preset--active' : ''}`}
                      onClick={() => applyFontPreset(preset)}
                    >
                      <div className="wl-font-preset-row">
                        <span style={{ fontFamily: preset.fonts.heading, fontSize: 18, fontWeight: 600 }}>
                          {t(`superAdmin.wl.font.${preset.id}.label`, { defaultValue: preset.label })}
                        </span>
                        {isActive && <span className="wl-font-preset-dot" aria-label={t('superAdmin.wl.fontActive')} />}
                      </div>
                      <div style={{ fontFamily: preset.fonts.body, fontSize: 12, color: 'var(--text-secondary)' }}>
                        {t(`superAdmin.wl.font.${preset.id}.description`, { defaultValue: preset.description })}
                      </div>
                      {/* Concrete preview of what the picked fonts look like, so the user
                          knows what to expect even before the right-rail re-renders. */}
                      <div className="wl-font-preset-sample">
                        <span style={{ fontFamily: preset.fonts.heading, fontWeight: 600 }}>Aa</span>
                        <span style={{ fontFamily: preset.fonts.body }}>{t('whiteLabel.theQuickBrownFox')}</span>
                        <span style={{ fontFamily: preset.fonts.mono }}>{'{ x }'}</span>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Type sample — heading, body and mono side by side in the
                  fonts currently selected. It used to be pinned above the
                  preview for every group, which cost vertical room on the
                  nine groups that are not about type; it lives here instead,
                  where choosing a font is the whole job. The rules that drive
                  it are injected with the rest of the preview CSS, keyed on
                  the data-sample attributes below. */}
              <div className="wl-type-sample">
                <div className="wl-type-sample-header">
                  <span className="wl-type-sample-eyebrow">{t('superAdmin.wl.typeSample')}</span>
                  <span className="wl-type-sample-trio">
                    <code>{merged.fonts?.heading}</code> · <code>{merged.fonts?.body}</code> · <code>{merged.fonts?.mono}</code>
                  </span>
                </div>
                <div className="wl-type-sample-row">
                  <span className="wl-type-sample-tag">{t('superAdmin.wl.typeHeading')}</span>
                  <span className="wl-type-sample-text" data-sample="heading" style={{ fontSize: 20, fontWeight: 700 }}>
                    {t('superAdmin.wl.typeHeadingSample')}
                  </span>
                </div>
                <div className="wl-type-sample-row">
                  <span className="wl-type-sample-tag">{t('superAdmin.wl.typeBody')}</span>
                  <span className="wl-type-sample-text" data-sample="body">
                    {t('superAdmin.wl.typeBodySample')}
                  </span>
                </div>
                <div className="wl-type-sample-row">
                  <span className="wl-type-sample-tag">{t('superAdmin.wl.typeMono')}</span>
                  <span className="wl-type-sample-text" data-sample="mono">
                    const brand = resolveBrand(tenant);
                  </span>
                </div>
              </div>
            </Field>
          </Section>

          <Section
            title={t('superAdmin.wl.advTypeTitle')}
            subtitle={t('superAdmin.wl.advTypeSub')}
          >
            <Field
              label={t('superAdmin.wl.monoFont')}
              affects={['chat']}
              modified={isFieldModified('fonts.mono')}
              onChipClick={onChipClick}
            >
              <Input
                value={overrides.fonts?.mono ?? ''}
                onChange={e => setFont('mono', e.target.value)}
                placeholder={merged.fonts?.mono || 'JetBrains Mono'}
              />
            </Field>
            <Field
              label={t('superAdmin.wl.accentSerif')}
              affects={['home', 'login']}
              modified={isFieldModified('fonts.accentSerif')}
              onChipClick={onChipClick}
            >
              <Input
                value={overrides.fonts?.accentSerif ?? ''}
                onChange={e => setFont('accentSerif', e.target.value)}
                placeholder={merged.fonts?.accentSerif || 'Arbutus Slab'}
              />
            </Field>
          </Section>

          </>)}

          {activeGroup === 'assets' && (<>
          {/* ── Logo ────────────────────────────────────────── */}
          <Section
            title={t('whiteLabel.logo')}
            subtitle={t('whiteLabel.logoSubtitle')}
            why={t('whiteLabel.whyLogo')}
          >
            <Field
              label={t('whiteLabel.logoWideWordmark')}
              hint={t('whiteLabel.shownOnTheLoginBranding')}
              affects={['login']}
              modified={isFieldModified('logo')}
              onFocus={() => previewFor(['login'])}
              onChipClick={onChipClick}
            >
              <Upload {...logoUploadProps}>
                <Button>{t('whiteLabel.uploadLogo')}</Button>
              </Upload>
              {(overrides.logo ?? defaultBrand.logo) && (
                <div className="wl-logo-preview">
                  <img src={overrides.logo ?? defaultBrand.logo} alt={t('whiteLabel.logo')} />
                </div>
              )}
            </Field>
          </Section>

          {/* ── Icon ────────────────────────────────────────── */}
          <Section
            title={t('whiteLabel.icon')}
            subtitle={t('whiteLabel.iconSubtitle')}
            why={t('whiteLabel.whyIcon')}
          >
            <Field
              label={t('whiteLabel.iconSquareMark')}
              hint={t('whiteLabel.squareMarkUsedOnThe')}
              affects={['sidebar', 'home', 'chat', 'app']}
              modified={isFieldModified('icon')}
              onFocus={() => previewFor(['sidebar'])}
              onChipClick={onChipClick}
            >
              <Upload {...iconUploadProps}>
                <Button>{t('whiteLabel.uploadIcon')}</Button>
              </Upload>
              {(overrides.icon ?? defaultBrand.icon) && (
                <div className="wl-logo-preview wl-icon-preview">
                  <img src={overrides.icon ?? defaultBrand.icon} alt={t('whiteLabel.icon')} />
                </div>
              )}
            </Field>
          </Section>

          {/* ── Favicon ─────────────────────────────────────── */}
          {/* Dedicated browser-tab favicon. Distinct from `icon` so a tenant
              can use a tightly-cropped square mark in the tab while keeping
              a padded/rounded icon inside the app. When unset, the favicon
              falls back to `icon`. */}
          <Section
            title={t('whiteLabel.favicon')}
            subtitle={t('whiteLabel.faviconSubtitle')}
            why={t('whiteLabel.whyFavicon')}
          >
            <Field
              label={t('whiteLabel.faviconBrowserTab')}
              hint={t('whiteLabel.squarePngSvgOrIco')}
              affects={['tab']}
              modified={isFieldModified('favicon')}
              onFocus={() => previewFor([])}
              onChipClick={onChipClick}
            >
              <Upload {...faviconUploadProps}>
                <Button>{t('whiteLabel.uploadFavicon')}</Button>
              </Upload>
              {(overrides.favicon ?? overrides.icon ?? defaultBrand.icon) && (
                <div className="wl-favicon-preview-row">
                  <div className="wl-favicon-tab-mock" aria-label={t('whiteLabel.browserTabPreview')}>
                    <img
                      className="wl-favicon-tab-mock-icon"
                      src={overrides.favicon ?? overrides.icon ?? defaultBrand.icon}
                      alt={t('whiteLabel.favicon')}
                    />
                    <span className="wl-favicon-tab-mock-title" dir="auto">
                      {overrides.pageTitle ?? defaultBrand.pageTitle}
                    </span>
                  </div>
                  {!overrides.favicon && (
                    <span className="wl-favicon-fallback-note">
                      {t('superAdmin.usingYourIconAsThe')}
                    </span>
                  )}
                </div>
              )}
            </Field>
          </Section>

          <Section
            title={t('superAdmin.wl.darkAssetsTitle')}
            subtitle={t('superAdmin.wl.darkAssetsSub')}
          >
            <Field
              label={t('superAdmin.wl.logoDark')}
              affects={['login', 'sidebar']}
              modified={isFieldModified('logoDark')}
              onChipClick={onChipClick}
            >
              <div className="wl-dark-asset-row">
                {merged.logoDark && (
                  <span className="wl-dark-asset-preview">
                    <img src={merged.logoDark} alt={t('superAdmin.wl.darkLogoAlt')} />
                  </span>
                )}
                <Upload {...makeUploadProps('logo', true)}>
                  <Button data-testid="wl-upload-logo-dark">{t('superAdmin.wl.uploadDarkLogo')}</Button>
                </Upload>
              </div>
            </Field>
            <Field
              label={t('superAdmin.wl.iconDark')}
              affects={['sidebar']}
              modified={isFieldModified('iconDark')}
              onChipClick={onChipClick}
            >
              <div className="wl-dark-asset-row">
                {merged.iconDark && (
                  <span className="wl-dark-asset-preview wl-dark-asset-preview--square">
                    <img src={merged.iconDark} alt={t('superAdmin.wl.darkIconAlt')} />
                  </span>
                )}
                <Upload {...makeUploadProps('icon', true)}>
                  <Button data-testid="wl-upload-icon-dark">{t('superAdmin.wl.uploadDarkIcon')}</Button>
                </Upload>
              </div>
            </Field>
          </Section>

          </>)}

          {activeGroup === 'agents' && (<>
          {/* ── Agent colors ────────────────────────────────── */}
          <Section
            title={t('whiteLabel.agentColors')}
            subtitle={t('whiteLabel.agentColorsSubtitle')}
            why={t('whiteLabel.whyAgentColors')}
          >
            <Field
              label={t('whiteLabel.agentCoinPalette')}
              hint={t('whiteLabel.defaultsToThePlatformS')}
              affects={['home', 'chat']}
              modified={isFieldModified('agentColors')}
              onFocus={() => previewFor(['home'])}
              onChipClick={onChipClick}
            >
              <AgentColorList
                value={overrides.agentColors ?? defaultBrand.agentColors ?? []}
                onChange={list => set('agentColors', list)}
              />
              <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  onClick={() => set('agentColors', defaultBrand.agentColors ?? [])}
                  data-testid="reset-agent-colors"
                >
                  {t('superAdmin.resetToDefaultPalette')}
                </Button>
                <span style={{ fontSize: 11, color: 'var(--text-tertiary)', alignSelf: 'center' }}>
                  {t('whiteLabel.loadsDefaultColors', { count: defaultBrand.agentColors?.length ?? 0 })}
                </span>
              </div>
            </Field>
          </Section>

          {/* ── Avatar templates — the images agents wear ───────────── */}
          <Section
            title={t('whiteLabel.agentAvatars')}
            subtitle={t('whiteLabel.agentAvatarsSubtitle')}
            why={t('whiteLabel.whyAgentAvatars')}
          >
            <Field
              label={t('whiteLabel.agentAvatarsLabel')}
              hint={t('whiteLabel.agentAvatarsHint')}
              affects={['home', 'agents', 'chat']}
              modified={isFieldModified('agentAvatars')}
              onFocus={() => previewFor(['home'])}
              onChipClick={onChipClick}
            >
              <AgentAvatarSets
                palette={overrides.agentColors ?? defaultBrand.agentColors ?? []}
                value={overrides.agentAvatars?.textures}
                onChange={textures => set('agentAvatars', { ...(overrides.agentAvatars ?? {}), textures })}
                previewBrand={merged}
              />
            </Field>
          </Section>

          {/* ── Visual treatments — per-brand visual feature toggles ──── */}
          <Section
            title={t('whiteLabel.visualTreatments')}
            subtitle={t('whiteLabel.visualTreatmentsSubtitle')}
            why={t('whiteLabel.whyVisualTreatments')}
          >
            <Field
              label={t('whiteLabel.homePageSemicircle')}
              hint={t('whiteLabel.decorativeHalfCircleThatDrops')}
              affects={['home']}
              modified={isFieldModified('homeSemicircle')}
              onChipClick={onChipClick}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <Switch
                  checked={!!(overrides.homeSemicircle?.enabled ?? defaultBrand.homeSemicircle?.enabled)}
                  onChange={enabled => set('homeSemicircle', {
                    ...(overrides.homeSemicircle ?? defaultBrand.homeSemicircle ?? {}),
                    enabled,
                  })}
                  data-testid="wl-home-semicircle-toggle"
                />
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }} dir="auto">
                  {(overrides.homeSemicircle?.enabled ?? defaultBrand.homeSemicircle?.enabled) ? t('common.visible') : t('common.hidden')}
                </span>

                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginInlineStart: 12 }}>
                  <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                    {t('superAdmin.colorOverrideLeaveBlankTo')}
                  </span>
                  <ColorPicker
                    value={overrides.homeSemicircle?.color ?? defaultBrand.homeSemicircle?.color ?? null}
                    onChange={c => set('homeSemicircle', {
                      ...(overrides.homeSemicircle ?? defaultBrand.homeSemicircle ?? {}),
                      color: c ? c.toHexString() : null,
                    })}
                    allowClear
                    showText
                  />
                </div>
              </div>
            </Field>

            <Field
              label={t('whiteLabel.inChatAgentTint')}
              hint={t('whiteLabel.whenOnTheConversationSurface')}
              affects={['chat']}
              modified={isFieldModified('chat')}
              onChipClick={onChipClick}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Switch
                  checked={!!(overrides.chat?.tintByAgent ?? defaultBrand.chat?.tintByAgent)}
                  onChange={tintByAgent => set('chat', {
                    ...(overrides.chat ?? defaultBrand.chat ?? {}),
                    tintByAgent,
                  })}
                  data-testid="wl-chat-tint-toggle"
                />
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }} dir="auto">
                  {(overrides.chat?.tintByAgent ?? defaultBrand.chat?.tintByAgent) ? t('whiteLabel.tintedByAgent') : t('whiteLabel.neutralWhite')}
                </span>
              </div>
            </Field>
          </Section>

          </>)}

          {activeGroup === 'onboarding' && (<>
          {/* ── Onboarding — a capability, saved on the spot ─────────── */}
          <Section
            title={t('whiteLabel.journey.tabTitle')}
            subtitle={t('whiteLabel.journey.tabSubtitle')}
            why={t('whiteLabel.journey.tabWhy')}
          >
            <Field
              label={t('whiteLabel.journey.sectionTitle')}
              hint={t('whiteLabel.journey.sectionHint')}
              affects={[]}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <Switch
                  checked={!!journeyOn}
                  loading={journeySaving || journeyOn === null}
                  disabled={journeyOn === null}
                  onChange={setJourneyEnabled}
                  data-testid="wl-journey-toggle"
                />
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                  {journeyOn === null
                    ? t('whiteLabel.checking')
                    : journeyOn
                      ? t('whiteLabel.journey.optOn')
                      : t('whiteLabel.journey.optOff')}
                </span>
                <span
                  style={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    color: 'var(--warning, #b7791f)',
                    border: '1px solid var(--warning, #b7791f)',
                    borderRadius: 20,
                    padding: '3px 9px',
                  }}
                >
                  In development
                </span>
              </div>
            </Field>
          </Section>
          </>)}

          {/* ── Connectors — per-tenant ceiling for the Google Drive connector ── */}
          {activeGroup === 'connectors' && (<>
          <Section
            title={t('whiteLabel.connectors.tabTitle')}
            subtitle={t('whiteLabel.connectors.tabSubtitle')}
            why={t('whiteLabel.connectors.tabWhy')}
          >
            <Field
              label={t('whiteLabel.connectors.currentLevel')}
              hint={t('whiteLabel.connectors.currentLevelHint')}
              affects={['chat']}
              modified={false}
              onChipClick={onChipClick}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }} data-testid="wl-drive-level">
                <span style={{ fontSize: 14, fontWeight: 700 }}>
                  {t(`whiteLabel.connectors.level.${driveLevel}`)}
                </span>
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                  {t(`whiteLabel.connectors.levelDetail.${driveLevel}`)}
                </span>
              </div>
            </Field>

            <Field
              label={t('whiteLabel.connectors.level1')}
              hint={t('whiteLabel.connectors.level1Hint')}
              affects={['chat']}
              modified={false}
              onChipClick={onChipClick}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Switch
                  checked={driveConnectorOn}
                  loading={driveConnectorSaving}
                  onChange={(on) => setDriveFlag(DRIVE_FLAG, on)}
                  data-testid="wl-drive-connector-toggle"
                />
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                  {driveConnectorOn
                    ? t('whiteLabel.connectors.level1On')
                    : t('whiteLabel.connectors.level1Off')}
                </span>
              </div>
            </Field>

            <Field
              label={t('whiteLabel.connectors.level2')}
              hint={t('whiteLabel.connectors.level2Hint')}
              affects={['chat']}
              modified={false}
              onChipClick={onChipClick}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <Switch
                  checked={driveEditingOn && driveConnectorOn}
                  disabled={!driveConnectorOn}
                  loading={driveConnectorSaving}
                  onChange={(on) => setDriveFlag(DRIVE_EDIT_FLAG, on)}
                  data-testid="wl-drive-editing-toggle"
                />
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                  {!driveConnectorOn
                    ? t('whiteLabel.connectors.level2NeedsConnector')
                    : driveEditingOn
                      ? t('whiteLabel.connectors.level2On')
                      : t('whiteLabel.connectors.level2Off')}
                </span>
              </div>
            </Field>
          </Section>

          {/* Developer access is not about Drive: its own section, so the
              Drive subtitle above never has to describe it. */}
          <Section
            title={t('whiteLabel.developerAccess.label')}
            subtitle={t('whiteLabel.developerAccess.subtitle')}
          >
            <Field
              label={t('whiteLabel.developerAccess.switchLabel')}
              hint={t('whiteLabel.developerAccess.hint')}
              affects={['chat']}
              modified={false}
              onChipClick={onChipClick}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <Switch
                  checked={devAccessOn === true}
                  loading={devAccessSaving || devAccessOn === null}
                  onChange={(on) => setDevAccess(on)}
                  data-testid="wl-developer-access-toggle"
                />
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                  {devAccessOn ? t('whiteLabel.developerAccess.on') : t('whiteLabel.developerAccess.off')}
                </span>
              </div>
            </Field>
          </Section>
          </>)}

          {activeGroup === 'defaults' && (
            <Section
              title={t('superAdmin.wl.groupDefaults')}
              subtitle={t('superAdmin.defaults.sectionSubtitle')}
            >
              <DefaultUserSettingsPanel
                tenantId={tenantId}
                tenantName={merged.name}
                onConfiguredChange={setDefaultsConfigured}
              />
            </Section>
          )}

          {activeGroup === 'email' && (<>
          {/* ── Transactional emails (password reset / invitations) ───── */}
          <EmailBrandingSection
            tenantId={tenantId}
            overrides={overrides}
            isFieldModified={isFieldModified}
            setEmail={setEmail}
            senderRevision={senderRevision}
          />
          {/* ── Which mail server sends it (own SMTP / inherited / platform) ── */}
          <EmailSendingSection
            tenantId={tenantId}
            onSenderChange={() => setSenderRevision(r => r + 1)}
          />
          {/* ── Whether the organizations under this one use these settings ── */}
          <EmailSharingSection
            tenantId={tenantId}
            revision={`${senderRevision}:${JSON.stringify(savedSnapshot.email ?? {})}`}
          />

          </>)}

          {/* ── Support reports, also to a Monday board (own / inherited / off) ── */}
          <div hidden={activeGroup !== 'support'}>
            <SupportBoardSection ref={supportRef} tenantId={tenantId} brandName={merged.name} onStateChange={setSupport} />
          </div>

          {activeGroup === 'documents' && (<>
          {/* ── Design skill — brand bundle for agent artifacts ──────────
              Self-contained (its own upload/save flow, separate from brand
              overrides). Always shown to super-admins; the per-tenant flag
              `modules.design_skills` controls whether agents actually use it. */}
          <DesignSkillPanel tenantId={tenantId} scope="own" />

          {/* ── Default for this tenant AND everything under it ────────────
              Used whenever an artifact would otherwise render plain — the
              tenant has no brand book of its own, or the user switched
              branding off. Inherited by the whole subtree, so setting it on
              a parent covers every tenant beneath; a child may set its own
              to override. */}
          <DesignSkillPanel tenantId={tenantId} scope="default" />

          </>)}

          {activeGroup === 'danger' && (
          <Section
            title={t('superAdmin.wl.danger.title')}
            subtitle={t('superAdmin.wl.danger.subtitle')}
          >
            <div className="wl-danger-card">
              <div className="wl-danger-text">
                <div className="wl-danger-title">{t('superAdmin.wl.danger.resetTitle')}</div>
                <div className="wl-danger-sub">{t('superAdmin.wl.danger.resetBody')}</div>
              </div>
              <Button danger onClick={handleReset} data-testid="wl-reset-brand">
                {t('superAdmin.wl.danger.resetTitle')}
              </Button>
            </div>
            <div className="wl-danger-card">
              <div className="wl-danger-text">
                <div className="wl-danger-title">{t('superAdmin.wl.danger.cascadeTitle')}</div>
                <div className="wl-danger-sub">{t('superAdmin.wl.danger.cascadeBody')}</div>
              </div>
              <Button
                loading={applyingToSubtree}
                onClick={handleApplyToDescendants}
                disabled={dirty}
                data-testid="wl-apply-to-subtenants"
              >
                {t('superAdmin.wl.danger.cascadeTitle')}
              </Button>
            </div>
          </Section>
          )}

          </div>

          {/* Save footer — the column's own footer, outside its scroll area,
              so it never scrolls away and never covers the last control.
              Hidden on the Default user settings group: nothing there is a
              brand override, so this bar could never save it. Leaving it up
              put two "Save" buttons on one screen, one of which does nothing
              for the fields in front of you — the worst kind of ambiguity in
              a settings page. */}
          {activeGroup === 'support' && support.editable && (
          <div className="wl-editor-actions" data-testid="wl-support-actions">
            <div className="wl-editor-actions-status">
              <div data-testid="wl-support-actions-status">
                {support.dirty ? t('whiteLabel.supportBoard.bar.unsaved') : t('whiteLabel.supportBoard.bar.upToDate')}
              </div>
              {dirty && (
                <div className="wl-editor-actions-aside">{t('whiteLabel.supportBoard.bar.brandElsewhere')}</div>
              )}
            </div>
            <Space wrap className="wl-editor-actions-buttons">
              {previewToggle}
              <Button size={footerSize} onClick={() => supportRef.current?.discard()} disabled={!support.dirty || support.saving}
                      data-testid="wl-support-discard">
                {t('whiteLabel.discard')}
              </Button>
              <Button size={footerSize} type="primary" loading={support.saving} disabled={!support.canSave}
                      onClick={() => { void supportRef.current?.save(); }} data-testid="wl-support-save">
                {t('whiteLabel.saveChanges')}
              </Button>
            </Space>
          </div>
          )}
          {activeGroup !== 'defaults' && activeGroup !== 'support' && (
          <div className="wl-editor-actions">
            <div className="wl-editor-actions-status">
              {support.dirty && (
                <div>
                  <button type="button" className="wl-editor-actions-elsewhere"
                          onClick={() => setActiveGroup('support')} data-testid="wl-support-unsaved-elsewhere">
                    {t('whiteLabel.supportBoard.bar.unsavedOpen')}
                  </button>
                </div>
              )}
              {dirty ? (
                <Tooltip
                  title={
                    <div style={{ maxWidth: 240 }}>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>{t('whiteLabel.changedFields')}</div>
                      <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                        {changeKeys.map(k => <li key={k} style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }}>{k}</li>)}
                      </ul>
                    </div>
                  }
                >
                  <span className="wl-editor-actions-changes" data-testid="wl-unsaved-summary">
                    {unsavedGroups.length
                      ? t('whiteLabel.workspace.unsavedIn', {
                        count: changeCount, groups: unsavedGroups.map(groupLabel).join(', '),
                      })
                      : t('whiteLabel.changesNotSaved', { count: changeCount })}
                  </span>
                </Tooltip>
              ) : <span data-testid="wl-unsaved-summary">{t('whiteLabel.workspace.allSaved')}</span>}
            </div>
            <Space wrap className="wl-editor-actions-buttons">
              {previewToggle}
              <Tooltip title={t('whiteLabel.rollBackTheFormTo')}>
                <Button size={footerSize} onClick={handleDiscard} disabled={!dirty}>{t('whiteLabel.discard')}</Button>
              </Tooltip>

              <Button size={footerSize} type="primary" loading={saving} onClick={handleSave} disabled={!dirty}
                      data-testid="wl-save">
                {t('whiteLabel.saveChanges')}
              </Button>
            </Space>
          </div>
          )}
          {(activeGroup === 'defaults' || (activeGroup === 'support' && !support.editable)) && (
          <div className="wl-editor-actions" data-testid="wl-own-save-actions">
            <div className="wl-editor-actions-status">
              {activeGroup === 'defaults' ? t('whiteLabel.workspace.savesOnItsOwn') : t('whiteLabel.workspace.nothingToSave')}
            </div>
            <Space wrap className="wl-editor-actions-buttons">{previewToggle}</Space>
          </div>
          )}
        </div>

        {/* ─── Right rail: live preview ─────────────────────── */}
        {showPreviewColumn && (
          <aside className="wl-editor-preview" aria-label={t('whiteLabel.livePreview')}>{previewBody}</aside>
        )}
      </div>
      )}

      {view === 'edit' && narrow && (
        <Drawer
          open={previewDrawer}
          onClose={() => setPreviewDrawer(false)}
          placement={endPlacement(direction)}
          width="min(560px, 94vw)"
          rootClassName="wl-preview-drawer"
          data-testid="wl-preview-drawer"
        >
          <div className="wl-editor-preview wl-editor-preview--drawer">{previewBody}</div>
        </Drawer>
      )}

      {view === 'presets' && (
        <div className="wl-substep">
          <SubStepHeader
            title={t('whiteLabel.browsePresets')}
            sub={t('whiteLabel.browsePresetsSub')}
          />
          <PresetGallery
            onApply={(p) => {
              setOverrides(p.overrides);
              triggerPulse();
              antdMessage.info(`Loaded "${p.name}" into the form. Click Save to apply.`);
              setView('edit');
            }}
          />
        </div>
      )}

      {view === 'save-as' && (
        <div className="wl-substep">
          <SubStepHeader
            title={t('superAdmin.wl.saveAs.headerTitle')}
            sub={t('superAdmin.wl.saveAs.headerSub')}
          />

          <div className="wl-saveas">
            <section className="sa-panel">
              <header className="sa-panel-head">
                <h3>{t('superAdmin.wl.saveAs.title')}</h3>
                <p>{t('superAdmin.wl.saveAs.subtitle')}</p>
              </header>

              <div className="sa-panel-body">
                <AccessRow
                  testId="saveas-row-name"
                  label={<label htmlFor="wl-saveas-name">{t('superAdmin.wl.saveAs.nameLabel')}</label>}
                  hint={t('superAdmin.wl.saveAs.nameHint')}
                >
                  <Input
                    id="wl-saveas-name"
                    className="wl-saveas-input"
                    value={saveAsName}
                    onChange={e => setSaveAsName(e.target.value)}
                    placeholder={t('superAdmin.wl.saveAs.namePlaceholder')}
                    maxLength={120}
                    autoFocus
                    data-testid="save-as-preset-name"
                  />
                </AccessRow>

                <AccessRow
                  testId="saveas-row-description"
                  label={<label htmlFor="wl-saveas-description">{t('superAdmin.wl.saveAs.descLabel')}</label>}
                  hint={t('superAdmin.wl.saveAs.descHint')}
                >
                  <Input.TextArea
                    id="wl-saveas-description"
                    className="wl-saveas-input"
                    value={saveAsDescription}
                    onChange={e => setSaveAsDescription(e.target.value)}
                    placeholder={t('superAdmin.wl.saveAs.descPlaceholder')}
                    rows={3}
                    maxLength={400}
                    data-testid="save-as-preset-description"
                  />
                </AccessRow>

                {/* What a save actually takes. Without this the button is a
                    leap of faith: the form holds unsaved edits, and "current"
                    means the editor, not the tenant on disk. */}
                <AccessRow
                  testId="saveas-row-contents"
                  label={t('superAdmin.wl.saveAs.capturedLabel')}
                  hint={t('superAdmin.wl.saveAs.capturedHint')}
                  footnote={
                    dirty
                      ? t('superAdmin.wl.saveAs.capturedFootnote', { count: changeCount })
                      : undefined
                  }
                >
                  <div className="wl-saveas-contents" data-testid="save-as-contents">
                    {changeKeys.length === 0 ? (
                      <span className="sa-access-hint">
                        {t('superAdmin.wl.saveAs.noOverrides')}
                      </span>
                    ) : (
                      changeKeys.map(k => <Tag key={k}>{k}</Tag>)
                    )}
                  </div>
                </AccessRow>
              </div>

              <footer className="sa-defaults-foot">
                <span className="sa-access-hint" dir="auto">
                  {t('whiteLabel.savedPresetsOfferedToAll')}
                </span>
                <Space>
                  <Button onClick={() => setView('edit')}>{t('common.cancel')}</Button>
                  <Button
                    type="primary"
                    loading={savingAs}
                    disabled={!saveAsName.trim()}
                    data-testid="save-as-preset-confirm"
                    onClick={async () => {
                      const name = saveAsName.trim();
                      if (!name) {
                        antdMessage.warning(t('whiteLabel.presetNeedsName'));
                        return;
                      }
                      setSavingAs(true);
                      try {
                        await createPreset({
                          preset_name: name,
                          preset_description: saveAsDescription.trim() || undefined,
                          brand_overrides: overrides,
                        });
                        antdMessage.success(`Saved “${name}” — it is now in the gallery.`);
                        setSaveAsName('');
                        setSaveAsDescription('');
                        setView('edit');
                      } catch (err: any) {
                        antdMessage.error(
                          `Could not save the preset: ${err?.response?.data?.error || err?.message || err}`,
                        );
                      } finally {
                        setSavingAs(false);
                      }
                    }}
                  >
                    {t('whiteLabel.savePreset')}
                  </Button>
                </Space>
              </footer>
            </section>
          </div>
        </div>
      )}

      {/* Portal-mounted floating PIP — opt-in pop-out preview the user
          can dock. Off by default; included here for completeness but
          stays in dock mode unless explicitly toggled. */}
      <FloatingPreviewWindow
        merged={merged}
        open={floatingOpen}
        onClose={() => setFloatingOpen(false)}
        surface={previewSurface}
        onSurfaceChange={setPreviewSurface}
        pulse={previewPulse}
      />
    </>
  );
}

/* ─── Type sample — always-visible heading + body + mono panel ────────
 *
 * Pinned at the top of the preview rail so picking a font preset has an
 * immediate, obvious visual response — designers were finding the font
 * changes invisible because the preview surfaces only showed fonts in
 * specific elements (tagline, agent name) and the user had to know to
 * look. This panel makes it impossible to miss.
 */
/* ─── Section / Field primitives ─────────────────────────────────────── */

/**
 * "Transactional emails" editor section: sender name, subjects, logo,
 * colors and footer for the password-reset / invitation emails, plus a
 * live preview. The preview iframe is rendered by the BACKEND through the
 * real Jinja templates (`POST /super-admin/email-preview`) with the
 * unsaved draft layered on top — byte-for-byte what recipients get, so
 * the editor can never drift from production emails.
 */
function EmailBrandingSection({
  tenantId, overrides, isFieldModified, setEmail, senderRevision,
}: {
  tenantId: string;
  overrides: BrandOverrides;
  isFieldModified: (path: string) => boolean;
  setEmail: (key: keyof NonNullable<BrandOverrides['email']>, value: string) => void;
  /** Bumped when the Email sending settings change: the preview's From follows. */
  senderRevision: number;
}) {
  const { t } = useLanguage();
  const email = overrides.email ?? {};
  const [previewKind, setPreviewKind] = useState<'reset' | 'invite'>('reset');
  // What each field resolves to TODAY (own brand → the company ancestor's
  // brand when this tenant has none → platform default). Used to pre-fill the inputs so they show current
  // state instead of empty boxes. Display only — an untouched field writes
  // nothing, so the tenant keeps inheriting.
  const [resolved, setResolved] = useState<Record<string, string | null>>({});
  // The fields this organization inherits, each with the organization it
  // comes from — the nearest one above that sets it and shares its email
  // settings (shared/services/email_inheritance.py).
  const [sources, setSources] = useState<Record<string, { tenant_id: string; tenant_name: string }>>({});
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  // The full From a real mail carries: display name <the address of the
  // sender it goes out through — own, inherited or the platform default>.
  const [previewFrom, setPreviewFrom] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  // Prove delivery, not just looks: the SAVED brand, through this server's
  // real backend, to the super admin's own inbox. The response says which
  // backend ran — a server set to `console` writes an outbox file and
  // delivers nothing, which is indistinguishable from a lost mail otherwise.
  const sendTestEmail = async () => {
    setSendingTest(true);
    try {
      const res = await api.post('/super-admin/email-test-send', {
        tenant_id: tenantId, template: previewKind,
      });
      if (res.data?.backend === 'smtp') {
        antdMessage.success(t('whiteLabel.testEmailSent', { to: res.data.to }));
      } else {
        antdMessage.warning(t('whiteLabel.testEmailConsole'));
      }
    } catch (err: any) {
      antdMessage.error(err?.response?.data?.error || t('whiteLabel.testEmailFailed'));
    } finally {
      setSendingTest(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/super-admin/tenants/${tenantId}/email-brand`);
        if (!cancelled) {
          setResolved(res.data.email_brand || {});
          setSources(res.data.sources || {});
        }
      } catch {
        // Non-fatal: fields fall back to empty + placeholder, as before.
        if (!cancelled) { setResolved({}); setSources({}); }
      }
    })();
    return () => { cancelled = true; };
  }, [tenantId, senderRevision]);

  // Value shown in an input: the explicit override if there is one, else the
  // inherited/resolved value. Clearing a field deletes the override, so the
  // box repopulates with what it falls back to — which is the honest display
  // of what will actually be sent.
  const shown = (key: string) =>
    (email as Record<string, string | undefined>)[key] ?? resolved[key] ?? '';
  // "Inherited from <org>" under a field this organization does not set.
  const inherited = (key: string) => {
    const source = (email as Record<string, string | undefined>)[key] ? undefined : sources[key];
    return source ? (
      <div className="wl-field-hint wl-email-inherited" data-testid={`wl-email-inherited-${key}`}>
        {t('whiteLabel.emailInheritedFrom', { org: bidiIsolate(source.tenant_name) })}
      </div>
    ) : null;
  };

  // Debounced backend render — refreshes as the draft changes. The draft
  // sent is everything email reads from the brand — the general name, company
  // name, logo and primary colour as well as the email group — so the preview
  // shows what Save would send even while the Identity or Colors sections are
  // edited and unsaved. Sending only the email group once let the preview
  // show one brand and the sent mail another (2026-09-24).
  const draftJson = JSON.stringify({
    name: overrides.name,
    companyName: overrides.companyName,
    logo: overrides.logo,
    colors: { primary: overrides.colors?.primary },
    email,
  });
  useEffect(() => {
    let cancelled = false;
    const handle = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const res = await api.post('/super-admin/email-preview', {
          tenant_id: tenantId,
          template: previewKind,
          overrides: JSON.parse(draftJson),
        });
        if (!cancelled) {
          setPreviewHtml(res.data.html);
          setPreviewSubject(res.data.subject);
          setPreviewFrom(res.data.from);
        }
      } catch {
        if (!cancelled) setPreviewHtml('');
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, 500);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [tenantId, previewKind, draftJson, senderRevision]);

  // Client-side read of an email logo file into the draft (persisted with
  // the normal brand-overrides Save). Raster formats only — most email
  // clients don't render SVG, which would silently fall back to text.
  const emailLogoUploadProps: UploadProps = {
    accept: 'image/png,image/jpeg,image/gif,image/webp',
    showUploadList: false,
    beforeUpload: (file) => {
      if (file.size > 200_000) {
        antdMessage.error(t('whiteLabel.emailLogoTooBig'));
        return false;
      }
      const reader = new FileReader();
      reader.onload = () => setEmail('logo', String(reader.result || ''));
      reader.readAsDataURL(file);
      return false;
    },
  };

  return (
    <Section
      title={t('whiteLabel.transactionalEmails')}
      subtitle={t('whiteLabel.emailsSubtitle')}
      why={t('whiteLabel.whyEmails')}
    >
      <Field
        label={t('whiteLabel.productNameInEmails')}
        hint={t('whiteLabel.whatTheProductIsCalled')}
        modified={isFieldModified('email.brandName')}
      >
        <Input dir="auto"
          value={shown('brandName')}
          placeholder="e.g. Acme Workspace"
          onChange={e => setEmail('brandName', e.target.value)}
          data-testid="wl-email-brand-name"
        />
        {inherited('brandName')}
      </Field>
      <Field
        label={t('whiteLabel.senderName')}
        hint={t('whiteLabel.theFromDisplayNameIn')}
        modified={isFieldModified('email.senderName')}
      >
        <Input dir="auto"
          value={shown('senderName')}
          placeholder="e.g. Acme Security"
          onChange={e => setEmail('senderName', e.target.value)}
          data-testid="wl-email-sender-name"
        />
        {inherited('senderName')}
      </Field>
      <Field
        label={t('whiteLabel.resetEmailSubject')}
        hint={t('whiteLabel.resetSubjectHint')}
        modified={isFieldModified('email.subjectReset')}
      >
        <Input dir="auto"
          value={shown('subjectReset')}
          placeholder={t('whiteLabel.resetYourBrandnamePassword')}
          onChange={e => setEmail('subjectReset', e.target.value)}
          data-testid="wl-email-subject-reset"
        />
        {inherited('subjectReset')}
      </Field>
      <Field
        label={t('whiteLabel.invitationEmailSubject')}
        hint={'Default: "You\'re invited to {brandName}"'}
        modified={isFieldModified('email.subjectInvite')}
      >
        <Input dir="auto"
          value={shown('subjectInvite')}
          placeholder={t('whiteLabel.youReInvitedToBrandname')}
          onChange={e => setEmail('subjectInvite', e.target.value)}
          data-testid="wl-email-subject-invite"
        />
        {inherited('subjectInvite')}
      </Field>
      <Field
        label={t('whiteLabel.emailLogo')}
        hint={t('whiteLabel.headerImagePngJpgRecommended')}
        modified={isFieldModified('email.logo')}
      >
        <Space>
          <Upload {...emailLogoUploadProps}>
            <Button size="small">{t('whiteLabel.uploadLogo')}</Button>
          </Upload>
          {shown('logo') && (
            <img
              src={shown('logo')}
              alt="email logo"
              style={{ height: 28, maxWidth: 140, objectFit: 'contain' }}
            />
          )}
          {email.logo && (
            <Button size="small" onClick={() => setEmail('logo', '')}>{t('whiteLabel.clear')}</Button>
          )}
        </Space>
        {inherited('logo')}
      </Field>
      <Field
        label={t('whiteLabel.headerButtonColor')}
        hint={t('whiteLabel.fallsBackToTheBrand')}
        modified={isFieldModified('email.primaryColor')}
      >
        <Space>
          <ColorPicker
            value={shown('primaryColor') || null}
            onChange={c => setEmail('primaryColor', c ? c.toHexString() : '')}
            allowClear
            showText
          />
          {email.primaryColor && (
            <Button size="small" onClick={() => setEmail('primaryColor', '')}>{t('whiteLabel.useBrandPrimary')}</Button>
          )}
        </Space>
        {inherited('primaryColor')}
      </Field>
      <Field
        label={t('whiteLabel.footerText')}
        hint={t('whiteLabel.legalAddressLineAtThe')}
        modified={isFieldModified('email.footerText')}
      >
        <Input dir="auto"
          value={shown('footerText')}
          placeholder="e.g. Acme Inc, 1 Main St, Tel Aviv"
          onChange={e => setEmail('footerText', e.target.value)}
          data-testid="wl-email-footer"
        />
        {inherited('footerText')}
      </Field>

      {/* Live preview — backend-rendered through the real templates. */}
      <div className="wl-field" style={{ marginTop: 8 }}>
        <div className="wl-field-row" style={{ alignItems: 'center' }}>
          <label className="wl-field-label">{t('whiteLabel.preview')}</label>
          <Select
            size="small"
            value={previewKind}
            onChange={v => setPreviewKind(v)}
            style={{ width: 180 }}
            options={[
              { value: 'reset', label: t('designSystem.passwordResetEmail') },
              { value: 'invite', label: t('designSystem.invitationEmail') },
            ]}
            data-testid="wl-email-preview-kind"
          />
          <Button
            size="small"
            onClick={sendTestEmail}
            loading={sendingTest}
            data-testid="wl-email-test-send"
          >
            {t('whiteLabel.sendTestEmail')}
          </Button>
        </div>
        {previewSubject && (
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '6px 0' }}>
            <strong>{t('whiteLabel.previewFrom')}</strong> <bdi dir="ltr" data-testid="wl-email-preview-from">{previewFrom}</bdi>
            {' · '}
            <strong>{t('whiteLabel.previewSubject')}</strong> <bdi>{previewSubject}</bdi>
          </div>
        )}
        <div style={{ position: 'relative', border: '1px solid var(--border-primary, #e5e7eb)', borderRadius: 8, overflow: 'hidden', background: 'var(--surface-secondary, #f4f5f7)' }}>
          {previewLoading && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
              <Spin />
            </div>
          )}
          <iframe
            title={t('whiteLabel.emailPreview')}
            sandbox=""
            srcDoc={previewHtml}
            style={{ width: '100%', height: 460, border: 0, opacity: previewLoading ? 0.4 : 1 }}
            data-testid="wl-email-preview-frame"
          />
        </div>
        <div className="wl-field-hint">
          {t('superAdmin.renderedByTheServerThrough')}
        </div>
      </div>
    </Section>
  );
}

/**
 * ContrastReadout — WCAG AA readout for the color pairs the brand actually
 * renders: button text on primary, primary-as-link on the light and dark
 * page backgrounds. Failing is allowed — but it is stated, at the field,
 * before saving (Shopify makes merchants find a third-party checker; we
 * ship one in the editor).
 */
function ContrastReadout({ merged }: { merged: BrandConfig }) {
  const { t } = useLanguage();
  // These two are the neutral page backgrounds a brand is read against when the
  // tenant has not set surfaces of its own, and the white below is the button
  // label colour the app actually paints — not brand choices. They are literals
  // because the WCAG luminance maths needs concrete RGB and cannot resolve a
  // CSS custom property.
  const lightBg = merged.lightMode?.bgPrimary ?? '#ffffff';
  const darkBg = merged.darkMode?.bgPrimary ?? '#111827';
  const pairs: Array<{ label: string; fg: string; bg: string }> = [
    { label: t('superAdmin.wl.contrastWhiteOnPrimary'), fg: '#ffffff', bg: merged.colors.primary },
    { label: t('superAdmin.wl.contrastPrimaryOnLight'), fg: merged.colors.primary, bg: lightBg },
    { label: t('superAdmin.wl.contrastPrimaryOnDark'), fg: merged.colors.primary, bg: darkBg },
  ];
  const rows = pairs.map(p => ({ ...p, ratio: contrastRatio(p.fg, p.bg) }));
  const allPass = rows.every(r => r.ratio !== null && r.ratio >= 4.5);
  return (
    <div
      className={`wl-contrast ${allPass ? 'wl-contrast--pass' : 'wl-contrast--fail'}`}
      data-testid="wl-contrast-readout"
    >
      <div className="wl-contrast-head">
        {allPass ? t('superAdmin.wl.contrastPass') : t('superAdmin.wl.contrastFail')}
      </div>
      <div className="wl-contrast-rows">
        {rows.map(r => (
          <div key={r.label} className="wl-contrast-row">
            <span
              className="wl-contrast-swatch"
              style={{ background: r.bg, color: r.fg }}
              aria-hidden
            >
              Aa
            </span>
            <span className="wl-contrast-label" dir="auto">{r.label}</span>
            <span className={`wl-contrast-ratio ${r.ratio !== null && r.ratio >= 4.5 ? 'wl-contrast-ratio--pass' : 'wl-contrast-ratio--fail'}`}>
              {r.ratio === null ? '—' : `${r.ratio.toFixed(1)}:1`}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Section({
  title, subtitle, why, children,
}: { title: string; subtitle?: string; why?: string; children: React.ReactNode }) {
  const { t } = useLanguage();
  const [whyOpen, setWhyOpen] = useState(false);
  return (
    <section className="wl-section">
      <header className="wl-section-header">
        <h4 className="wl-section-title">{title}</h4>
        {subtitle && <p className="wl-section-subtitle">{subtitle}</p>}
        {why && (
          <button
            type="button"
            className="wl-why-toggle"
            onClick={() => setWhyOpen(o => !o)}
            aria-expanded={whyOpen}
          >
            {whyOpen ? t('whiteLabel.hideExplanation') : t('whiteLabel.whyDoesThisMatter')}
          </button>
        )}
        {why && whyOpen && <p className="wl-section-why">{why}</p>}
      </header>
      <div className="wl-section-body">{children}</div>
    </section>
  );
}

interface FieldProps {
  label: string;
  hint?: string;
  /** Surfaces this field renders on. Drives the "Shows on:" chips +
   *  auto-switches the preview pane on focus. */
  affects?: Surface[];
  /** Show a small dot when the field's value differs from the saved snapshot. */
  modified?: boolean;
  /** Fired when ANY child element gains focus — auto-jumps preview to surface. */
  onFocus?: () => void;
  /** Click handler for the "Shows on" chips. */
  onChipClick?: (s: Surface) => void;
  /** Optional collapsible "Why change this?" essay. */
  whyChange?: string;
  children: React.ReactNode;
}

function Field({ label, hint, affects, modified, onFocus, onChipClick, whyChange, children }: FieldProps) {
  const { t } = useLanguage();
  const [whyOpen, setWhyOpen] = useState(false);
  return (
    <div
      className={`wl-field ${modified ? 'wl-field--modified' : ''}`}
      onFocus={onFocus}
    >
      <div className="wl-field-row">
        <label className="wl-field-label">
          {label}
          {modified && (
            <Tooltip title={t('whiteLabel.unsavedChangeClickSaveAt')}>
              <span className="wl-field-modified-dot" aria-label="modified" />
            </Tooltip>
          )}
        </label>
        {affects && affects.length > 0 && <AffectsBadges surfaces={affects} onClick={onChipClick} />}
      </div>
      {children}
      {hint && <div className="wl-field-hint">{hint}</div>}
      {whyChange && (
        <div className="wl-field-why">
          <button
            type="button"
            className="wl-why-toggle wl-why-toggle--field"
            onClick={() => setWhyOpen(o => !o)}
            aria-expanded={whyOpen}
          >
            {whyOpen ? t('whiteLabel.hideContext') : t('whiteLabel.whyChangeThis')}
          </button>
          {whyOpen && <p className="wl-field-why-body">{whyChange}</p>}
        </div>
      )}
    </div>
  );
}

function AffectsBadges({
  surfaces, onClick,
}: { surfaces: Surface[]; onClick?: (s: Surface) => void }) {
  const { t } = useLanguage();
  // One record per badge row, not one per chip: the Colors group renders 18 of
  // these and re-renders on every frame of a ColorPicker drag.
  const labels = useMemo(() => surfaceLabel(t), [t]);
  return (
    <div className="wl-field-affects" aria-label={t('whiteLabel.whereThisChangeShowsUp')}>
      <span className="wl-field-affects-label">{t('whiteLabel.showsOn')}</span>
      {surfaces.map(s => {
        const interactive = onClick && s !== 'tab' && s !== 'app';
        const Tag = interactive ? 'button' : 'span';
        return (
          <Tooltip key={s} title={interactive ? t('whiteLabel.jumpPreviewTo', { surface: labels[s] }) : labels[s]}>
            <Tag
              type={interactive ? 'button' : undefined}
              className={`wl-affects-chip wl-affects-chip--${s}${interactive ? ' wl-affects-chip--clickable' : ''}`}
              onClick={interactive ? () => onClick!(s) : undefined}
            >
              {labels[s]}
            </Tag>
          </Tooltip>
        );
      })}
    </div>
  );
}

function ColorPickerInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="wl-color-picker">
      <ColorPicker value={value} onChange={(c) => onChange(c.toHexString())} />
      <Input dir="auto" value={value} onChange={e => onChange(e.target.value)} style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }} />
    </div>
  );
}

function FeatureBulletEditor({ value, onChange }: { value: BrandLoginFeature[]; onChange: (v: BrandLoginFeature[]) => void }) {
  const { t } = useLanguage();
  // One options array for the whole editor: antd's Select memoizes on the
  // array identity, and this used to be rebuilt once per feature bullet.
  const tokenOptions = useMemo(() => colorTokenOptions(t), [t]);
  function update(i: number, patch: Partial<BrandLoginFeature>) {
    onChange(value.map((f, idx) => idx === i ? { ...f, ...patch } : f));
  }
  function add() {
    if (value.length >= 8) return;
    onChange([...value, { label: t('designSystem.newFeature'), color: 'primary' }]);
  }
  function remove(i: number) {
    onChange(value.filter((_, idx) => idx !== i));
  }
  // The label field accepts EITHER an i18n key (e.g.
  // `login.features.tenantAware`) OR a raw string. At login-page
  // render time the renderer calls `t(label)`: if the key resolves,
  // its translation shows; otherwise i18next returns the raw input
  // unchanged. Both modes ship in the default brand. Without the
  // resolved preview below, an editor sees the raw key and assumes
  // the system is leaking variable names - this preview line shows
  // exactly what the end-user will read on the login page.
  function looksLikeI18nKey(s: string): boolean {
    return /^[a-z][a-zA-Z0-9_]*(\.[a-z][a-zA-Z0-9_]*)+$/.test(s.trim());
  }
  return (
    <div className="wl-feature-list">
      {value.map((f, i) => {
        const isKey = looksLikeI18nKey(f.label);
        const resolved = isKey ? t(f.label) : null;
        const previewDiffers = !!resolved && resolved !== f.label;
        return (
          <div key={i} style={{ marginBottom: 8 }}>
            <div className="wl-feature-row">
              <Input dir="auto"
                value={f.label}
                onChange={e => update(i, { label: e.target.value })}
                placeholder={t('whiteLabel.labelOrI18nKeyLogin')}
                style={{ flex: 1 }}
              />
              <Select
                value={f.color || 'primary'}
                onChange={v => update(i, { color: v })}
                options={tokenOptions}
                style={{ width: 110 }}
              />
              <Button onClick={() => remove(i)} type="text" danger>×</Button>
            </div>
            {previewDiffers && (
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginInlineStart: 2, marginBlockStart: 2 }}>
                <span style={{ opacity: 0.6 }}>{t('whiteLabel.endUserSees')}</span>{' '}
                <span style={{ fontStyle: 'italic' }}>"{resolved}"</span>
              </div>
            )}
          </div>
        );
      })}
      <Button onClick={add} disabled={value.length >= 8} block>{t('whiteLabel.addFeature')}</Button>
    </div>
  );
}

function AgentColorList({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const { t } = useLanguage();
  function update(i: number, hex: string) {
    onChange(value.map((c, idx) => idx === i ? hex : c));
  }
  function add() {
    onChange([...value, '#6b7280']);
  }
  function remove(i: number) {
    onChange(value.filter((_, idx) => idx !== i));
  }
  return (
    <div className="wl-agent-colors">
      {value.map((c, i) => (
        <div key={i} className="wl-agent-color-row">
          <ColorPicker value={c} onChange={(col) => update(i, col.toHexString())} />
          <Input dir="auto" value={c} onChange={e => update(i, e.target.value)} style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, flex: 1 }} />
          <Button onClick={() => remove(i)} type="text" danger>×</Button>
        </div>
      ))}
      <Button onClick={add} block>{t('whiteLabel.addColor')}</Button>
    </div>
  );
}
